import React, { useState, useRef, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserContext } from '../App';
import { brandIdentity } from '../theme/brandIdentity';
import Logo from './Logo';

function GestionFlujos() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const canvasRef = useRef(null);

  // Estados principales
  const [flujoActual, setFlujoActual] = useState(null);
  const [flujos, setFlujos] = useState([]);
  const [cargandoFlujos, setCargandoFlujos] = useState(false);
  const [errorFlujos, setErrorFlujos] = useState(false);
  const [bloques, setBloques] = useState([]);
  const [bloqueSeleccionado, setBloqueSeleccionado] = useState(null);
  const [conexionSeleccionada, setConexionSeleccionada] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mostrarSelectorFlujo, setMostrarSelectorFlujo] = useState(false);
  const [dragOverCanvas, setDragOverCanvas] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Estados para modal de nuevo flujo
  const [mostrarModalNuevoFlujo, setMostrarModalNuevoFlujo] = useState(false);
  const [datosNuevoFlujo, setDatosNuevoFlujo] = useState({
    nombre: '',
    descripcion: '',
    categoria: ''
  });
  const [erroresModalFlujo, setErroresModalFlujo] = useState({});

  // Estados para catégorias
  const [categorias, setCategorias] = useState([]);
  const [cargandoCategorias, setCargandoCategorias] = useState(false);
  const [errorCategorias, setErrorCategorias] = useState(false);

  // Estados para formularios y contratos
  const [formularios, setFormularios] = useState([]);
  const [cargandoFormularios, setCargandoFormularios] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [cargandoContratos, setCargandoContratos] = useState(false);

  // Estados para conexiones
  const blockRefs = useRef({}); // id -> DOM node
  const [edges, setEdges] = useState([]); // { id, from, fromSide: 'left'|'right', to, toSide: 'left'|'right' }
  const [linking, setLinking] = useState(null);
  // linking = { fromId, fromSide, toPos: {x,y} }
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, targetId: null });
  const [handleHover, setHandleHover] = useState(null); // para feedback visual

  // Estados para panning del canvas
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const canvasContainerRef = useRef(null);

  // Estado y refs para zoom
  const [zoom, setZoom] = useState(1.0);
  const zoomRef = useRef(1.0);
  const panOffsetRef = useRef({ x: 0, y: 0 });

  // Helpers para actualizar zoom y panOffset manteniendo refs sincronizadas
  const actualizarZoom = (val) => {
    zoomRef.current = val;
    setZoom(val);
  };
  const actualizarPanOffset = (val) => {
    panOffsetRef.current = val;
    setPanOffset(val);
  };

  // Estados para modal de confirmación de eliminación
  const [mostrarModalEliminacion, setMostrarModalEliminacion] = useState(false);
  const [flujoAEliminar, setFlujoAEliminar] = useState(null);

  // Tamaño fijo del mundo canvas (independiente de la resolución del monitor)
  const CANVAS_WORLD_WIDTH = 4000;
  const CANVAS_WORLD_HEIGHT = 3000;
  const ZOOM_MIN = 0.25;
  const ZOOM_MAX = 2.0;
  const ZOOM_STEP = 0.1;

  // Definición de tipos de bloques
  const tiposBloques = {
    imei_identifier: {
      id: 'imei_identifier',
      nombre: 'Identificar IMEI',
      descripcion: 'Identifica el IMEI del dispositivo por medio de una foto, también permite identificar la referencia del dispositivo.',
      color: '#6366F1',
      icono: '📱'
    },
    imei_validation: {
      id: 'imei_validation',
      nombre: 'Validar IMEI',
      descripcion: 'Valida que el IMEI del dispositivo no se encuentre en bases de datos negativas locales por medio de una captura de pantalla subida por el usuario.',
      color: '#10B981',
      icono: '🔍'
    },
    device_identifier: {
      id: 'device_identifier',
      nombre: 'Identificar dispositivo',
      descripcion: 'Identifica el serial del dispositivo por medio de una foto.',
      color: '#F59E0B',
      icono: '🔢'
    },
    select_device_manual: {
      id: 'select_device_manual',
      nombre: 'Seleccionar dispositivo manualmente',
      descripcion: 'Permite seleccionar la referencia del dispositivo por medio de listas desplegables.',
      color: '#8B5CF6',
      icono: '📋'
    },
    show_qr: {
      id: 'show_qr',
      nombre: 'Conectar dispositivo',
      descripcion: 'Permite conectar el dispositivo a retomar con el flujo por medio de la lectura de un código QR (Bloque necesario para realizar la prueba funcional física).',
      color: '#EF4444',
      icono: '📱'
    },
    pre_offer: {
      id: 'pre_offer',
      nombre: 'Mostrar pre oferta',
      descripcion: 'Muestra la pre oferta al usuario.',
      color: '#06B6D4',
      icono: '💰'
    },
    form_cosmetic: {
      id: 'form_cosmetic',
      nombre: 'Formulario cosmético',
      descripcion: 'Muestra un formulario de preguntas sobre el estado cosmético del dispositivo.',
      color: '#84CC16',
      icono: '✨',
      propiedades: ['form_id']
    },
    form_functional: {
      id: 'form_functional',
      nombre: 'Formulario funcional',
      descripcion: 'Muestra un formulario de preguntas sobre el estado funcional del dispositivo.',
      color: '#10B981',
      icono: '⚙️',
      propiedades: ['form_id']
    },
    diagnosis_screen: {
      id: 'diagnosis_screen',
      nombre: 'Prueba funcional física',
      descripcion: 'Realiza una prueba funcional del dispositivo desde el celular a retomar usando la aplicación "Refurbi".',
      color: '#F97316',
      icono: '🔧'
    },
    device_photos: {
      id: 'device_photos',
      nombre: 'Tomar fotos del dispositivo',
      descripcion: 'Permite tomarle fotos al dispositivo para guardar registro de su condición.',
      color: '#EC4899',
      icono: '📸'
    },
    offer_screen: {
      id: 'offer_screen',
      nombre: 'Mostrar oferta final',
      descripcion: 'Muestra la oferta final al usuario.',
      color: '#10B981',
      icono: '💵'
    },
    get_customer_id: {
      id: 'get_customer_id',
      nombre: 'Obtener ID del usuario',
      descripcion: 'Permite obtener los datos de identificación del cliente por medio de una foto de su documento nacional, si luego de tres intentos tomando fotos no se logra extraer correctamente la información el usuario puede escribir estos datos a mano.',
      color: '#6366F1',
      icono: '🆔'
    },
    customer_data: {
      id: 'customer_data',
      nombre: 'Obtener datos del cliente',
      descripcion: 'Permite obtener datos del cliente por medio de un formulario personalizado por cada país.',
      color: '#8B5CF6',
      icono: '👤'
    },
    contract_signature: {
      id: 'contract_signature',
      nombre: 'Mostrar y firmar contrato',
      descripción: 'Muestra el contrato asignado y solicita la firma del cliente, si este contrato contiene algún tipo de bono solo se mostrara cuando el contrato se suba a los servidores.',
      color: '#059669',
      icono: '📝',
      propiedades: ['contract_id']
    },
    aditional_docs: {
      id: 'aditional_docs',
      nombre: 'Documentos / Fotos adicionales',
      descripcion: 'Permite solicitar al usuario que suba archivos adicionales: documentos (PDF, Word), fotos o ambos.',
      color: '#14B8A6',
      icono: '📎',
      propiedades: ['title', 'count', 'description', 'types_allowed', 'mandatory']
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: brandIdentity.colors.backgroundLight,
      fontFamily: brandIdentity.typography.fontFamily
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: brandIdentity.spacing.md,
      backgroundColor: brandIdentity.colors.white,
      borderBottom: '1px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: brandIdentity.typography.sizes.h1,
      fontWeight: brandIdentity.typography.weights.bold,
      color: brandIdentity.colors.textDark,
      margin: 0
    },
    mainContent: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden'
    },
    sidebar: {
      width: '300px',
      backgroundColor: brandIdentity.colors.white,
      borderRight: '1px solid #E5E7EB',
      padding: brandIdentity.spacing.md,
      overflowY: 'auto'
    },
    canvas: {
      flex: 1,
      position: 'relative',
      backgroundColor: '#FAFAFA',
      backgroundImage: `
        radial-gradient(circle, #E5E7EB 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px',
      overflow: 'hidden'
    },
    propertiesPanel: {
      width: '300px',
      backgroundColor: brandIdentity.colors.white,
      borderLeft: '1px solid #E5E7EB',
      padding: brandIdentity.spacing.md,
      overflowY: 'auto'
    },
    sectionTitle: {
      fontSize: brandIdentity.typography.sizes.h2,
      fontWeight: brandIdentity.typography.weights.semibold,
      color: brandIdentity.colors.textDark,
      marginBottom: brandIdentity.spacing.md,
      borderBottom: '2px solid #E5E7EB',
      paddingBottom: brandIdentity.spacing.sm
    },
    backButton: {
      backgroundColor: brandIdentity.colors.primary,
      color: brandIdentity.colors.white,
      border: 'none',
      padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
      borderRadius: '8px',
      fontSize: brandIdentity.typography.sizes.body,
      fontWeight: brandIdentity.typography.weights.medium,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: brandIdentity.typography.fontFamily
    },
    selectorFlujos: {
      display: 'flex',
      alignItems: 'center',
      gap: brandIdentity.spacing.md
    },
    selectorContainer: {
      position: 'relative'
    },
    selectorButton: {
      backgroundColor: brandIdentity.colors.white,
      border: `2px solid ${brandIdentity.colors.primary}`,
      color: brandIdentity.colors.primary,
      padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
      borderRadius: '8px',
      fontSize: brandIdentity.typography.sizes.body,
      fontWeight: brandIdentity.typography.weights.medium,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: brandIdentity.typography.fontFamily,
      minWidth: '200px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: brandIdentity.colors.white,
      border: `1px solid #E5E7EB`,
      borderRadius: '8px',
      marginTop: '4px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      maxHeight: '300px',
      overflowY: 'auto'
    },
    dropdownItem: {
      padding: brandIdentity.spacing.md,
      cursor: 'pointer',
      borderBottom: '1px solid #F3F4F6',
      transition: 'all 0.2s ease'
    },
    nuevoFlujoButton: {
      backgroundColor: brandIdentity.colors.secondary,
      color: brandIdentity.colors.white,
      border: 'none',
      padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
      borderRadius: '8px',
      fontSize: brandIdentity.typography.sizes.body,
      fontWeight: brandIdentity.typography.weights.medium,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: brandIdentity.typography.fontFamily
    },
    bloqueHerramienta: {
      backgroundColor: brandIdentity.colors.white,
      border: '2px solid #E5E7EB',
      borderRadius: '12px',
      padding: brandIdentity.spacing.sm,
      marginBottom: brandIdentity.spacing.sm,
      cursor: 'grab',
      transition: 'all 0.2s ease',
      userSelect: 'none',
      position: 'relative'
    },
    bloqueIcono: {
      fontSize: '24px',
      display: 'block',
      textAlign: 'center',
      marginBottom: brandIdentity.spacing.xs
    },
    bloqueNombre: {
      fontSize: brandIdentity.typography.sizes.small,
      fontWeight: brandIdentity.typography.weights.medium,
      color: brandIdentity.colors.textDark,
      textAlign: 'center',
      marginBottom: brandIdentity.spacing.xs,
      lineHeight: '1.2'
    },
    bloqueDescripcion: {
      fontSize: brandIdentity.typography.sizes.xs,
      color: brandIdentity.colors.textLight,
      textAlign: 'center',
      lineHeight: '1.2',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      height: '24px'
    },
    categoriaHerramientas: {
      marginBottom: brandIdentity.spacing.lg
    },
    categoriaTitulo: {
      fontSize: brandIdentity.typography.sizes.body,
      fontWeight: brandIdentity.typography.weights.semibold,
      color: brandIdentity.colors.textDark,
      marginBottom: brandIdentity.spacing.sm,
      padding: `${brandIdentity.spacing.xs} 0`,
      borderBottom: `2px solid #E5E7EB`
    },
    bloqueCanvas: {
      position: 'absolute',
      backgroundColor: brandIdentity.colors.white,
      border: '2px solid #E5E7EB',
      borderRadius: '12px',
      padding: brandIdentity.spacing.md,
      minWidth: '180px',
      cursor: 'move',
      userSelect: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    bloqueCanvasSeleccionado: {
      borderColor: brandIdentity.colors.primary,
      boxShadow: `0 4px 16px rgba(99, 102, 241, 0.3)`
    },
    bloqueCanvasIcono: {
      fontSize: '32px',
      textAlign: 'center',
      marginBottom: brandIdentity.spacing.sm
    },
    bloqueCanvasNombre: {
      fontSize: brandIdentity.typography.sizes.body,
      fontWeight: brandIdentity.typography.weights.medium,
      color: brandIdentity.colors.textDark,
      textAlign: 'center',
      marginBottom: brandIdentity.spacing.xs
    },
    bloqueCanvasTipo: {
      fontSize: brandIdentity.typography.sizes.xs,
      color: brandIdentity.colors.textLight,
      textAlign: 'center',
      fontStyle: 'italic'
    },
    canvasVacio: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      color: brandIdentity.colors.textLight,
      fontSize: brandIdentity.typography.sizes.h2,
      userSelect: 'none'
    },
    dragOver: {
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      borderColor: brandIdentity.colors.primary
    },
    handles: {
      out: {
        position: 'absolute',
        right: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: brandIdentity.colors.primary,
        border: `2px solid ${brandIdentity.colors.white}`,
        cursor: 'crosshair',
        boxShadow: '0 0 0 2px rgba(99,102,241,0.2)'
      },
      in: {
        position: 'absolute',
        left: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: brandIdentity.colors.white,
        border: `2px solid ${brandIdentity.colors.primary}`,
        cursor: 'crosshair',
        boxShadow: '0 0 0 2px rgba(99,102,241,0.2)'
      }
    },
    edgeSvg: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    }
  };

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }

      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // ============================================
  // FUNCIONES API PARA FLUJOS
  // ============================================

  // Función para generar flow_path ordenado
  const generateFlowPath = (bloques, edges) => {
    const path = [];
    const visited = new Set();

    // Encontrar bloque de inicio
    const inicioBloque = bloques.find(b => b.tipo === 'inicio');
    if (!inicioBloque) return path;

    let currentBloque = inicioBloque;
    let stepOrder = 1;

    while (currentBloque && !visited.has(currentBloque.id)) {
      visited.add(currentBloque.id);

      // Encontrar siguiente bloque
      const nextConnection = edges.find(e => e.from === currentBloque.id);
      const nextBlocks = nextConnection ? [nextConnection.to] : [];

      path.push({
        step_order: stepOrder++,
        block_id: currentBloque.id,
        block_type: currentBloque.tipo,
        parameters: currentBloque.propiedades || {},
        next_blocks: nextBlocks
      });

      // Avanzar al siguiente bloque
      currentBloque = nextBlocks.length > 0 ?
        bloques.find(b => b.id === nextBlocks[0]) : null;
    }

    return path;
  };

  // Función para extraer assets necesarios
  const extractFlowAssets = (bloques) => {
    const assets = {
      forms: [],
      contracts: [],
      device_models: [],
      pricing_tables: []
    };

    bloques.forEach(bloque => {
      if ((bloque.tipo === 'form_cosmetic' || bloque.tipo === 'form_functional') && bloque.propiedades?.form_id) {
        assets.forms.push({
          form_id: bloque.propiedades.form_id,
          required: true,
          cache_ttl: 3600
        });
      }

      if (bloque.tipo === 'contract_signature' && bloque.propiedades?.contract_id) {
        assets.contracts.push({
          contract_id: bloque.propiedades.contract_id,
          required: true,
          cache_ttl: 7200
        });
      }
    });

    return assets;
  };

  // Función para transformar datos para la base de datos
  const transformForDatabase = (flujoData, bloques, edges) => {
    const flowPath = generateFlowPath(bloques, edges);
    const flowAssets = extractFlowAssets(bloques);

    return {
      flow_name: flujoData.nombre,
      description: flujoData.descripcion || '',
      category: flujoData.categoria,
      flow_data: {
        blocks: bloques.map(bloque => ({
          id: bloque.id,
          type: bloque.tipo,
          name: bloque.nombre,
          position: bloque.posicion,
          properties: bloque.propiedades || {}
        })),
        connections: edges.map(edge => ({
          id: edge.id,
          from: edge.from,
          from_side: edge.fromSide,
          to: edge.to,
          to_side: edge.toSide
        }))
      },
      flow_path: flowPath,
      flow_assets: flowAssets,
      metadata: {
        total_steps: flowPath.length,
        estimated_duration: flowPath.length * 60,
        requires_forms: flowAssets.forms.length > 0,
        requires_contracts: flowAssets.contracts.length > 0,
        block_types: [...new Set(bloques.map(b => b.tipo))]
      }
    };
  };

  // Función para cargar flujos del backend
  const cargarFlujos = async () => {
    setCargandoFlujos(true);
    setErrorFlujos(false);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flows',
        { headers }
      );

      const flujosCargados = (response.data.flows || []).map(flujo => ({
        id: flujo.flow_id,
        nombre: flujo.flow_name,
        descripcion: flujo.description || '',
        categoria: flujo.category || '',
        fechaCreacion: flujo.created_at,
        fechaModificacion: flujo.updated_at,
        bloques: flujo.flow_data?.blocks?.map(block => ({
          id: block.id,
          tipo: block.type,
          nombre: block.name,
          posicion: block.position,
          propiedades: block.properties || {}
        })) || [],
        conexiones: flujo.flow_data?.connections?.map(conn => ({
          id: conn.id,
          from: conn.from,
          fromSide: conn.from_side,
          to: conn.to,
          toSide: conn.to_side
        })) || []
      }));

      setFlujos(flujosCargados);
      setErrorFlujos(false);
    } catch (error) {
      console.error('Error al cargar flujos:', error);
      setFlujos([]);
      setErrorFlujos(true);
      mostrarMensaje('❌ Error al cargar los flujos', 'error');
    } finally {
      setCargandoFlujos(false);
    }
  };

  // Función para crear un nuevo flujo
  const crearFlujo = async (datosFlulo) => {
    try {
      const headers = await getAuthHeaders();
      const payload = transformForDatabase(datosFlulo,
        [
          { id: 'inicio', tipo: 'inicio', nombre: 'Inicio del flujo', posicion: { x: 100, y: 50 }, propiedades: {} },
          { id: 'fin', tipo: 'fin', nombre: 'Fin del flujo', posicion: { x: 100, y: 150 }, propiedades: {} }
        ],
        []
      );

      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flows',
        payload,
        { headers }
      );

      return {
        id: response.data.flow_id,
        nombre: payload.flow_name,
        descripcion: payload.description,
        categoria: payload.category,
        fechaCreacion: response.data.created_at || new Date().toISOString(),
        fechaModificacion: response.data.updated_at || new Date().toISOString(),
        bloques: [
          { id: 'inicio', tipo: 'inicio', nombre: 'Inicio del flujo', posicion: { x: 100, y: 50 }, propiedades: {} },
          { id: 'fin', tipo: 'fin', nombre: 'Fin del flujo', posicion: { x: 100, y: 150 }, propiedades: {} }
        ]
      };
    } catch (error) {
      console.error('Error al crear flujo:', error);
      throw error;
    }
  };

  // Función para actualizar flujo existente
  const actualizarFlujo = async (flujoId, datosFlulo, bloques, edges) => {
    try {
      const headers = await getAuthHeaders();
      const payload = transformForDatabase(datosFlulo, bloques, edges);

      // Si el flujo ya tiene un ID, es una actualización (PUT)
      if (flujoId) {
        payload.flow_id = flujoId;
      }

      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flows',
        payload,
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error('Error al actualizar flujo:', error);
      throw error;
    }
  };

  // Función para eliminar flujo
  const eliminarFlujoBackend = async (flujoId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flows/${flujoId}`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Error al eliminar flujo:', error);
      throw error;
    }
  };

  // Función para cargar categorías
  const cargarCategorias = async () => {
    setCargandoCategorias(true);
    setErrorCategorias(false);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        { headers }
      );

      // Transformar array simple a objetos con id y name
      const categoriasTransformadas = (response.data || []).map(categoria => ({
        id: categoria.toLowerCase().replace(/\s+/g, '-'),
        name: categoria
      }));

      setCategorias(categoriasTransformadas);
      setErrorCategorias(false);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      setCategorias([]);
      setErrorCategorias(true);
    } finally {
      setCargandoCategorias(false);
    }
  };

  // Función para cargar formularios de una categoría específica
  const cargarFormularios = async (categoria) => {
    if (!categoria) {
      setFormularios([]);
      return;
    }

    setCargandoFormularios(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        {
          headers,
          params: {
            mode: 'category_collections',
            category: categoria,
            limit: 50
          }
        }
      );

      setFormularios(response.data.collections || []);
    } catch (error) {
      console.error('Error al cargar formularios:', error);
      setFormularios([]);
    } finally {
      setCargandoFormularios(false);
    }
  };

  // Función para cargar contratos
  const cargarContratos = async () => {
    setCargandoContratos(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates?mode=headers',
        { headers }
      );

      if (response.status === 200 && response.data.item?.contracts) {
        const contractsList = [];
        const contractsObj = response.data.item.contracts;

        // Procesar los contratos
        Object.entries(contractsObj).forEach(([contractId, contractData]) => {
          if (contractData.M) {
            // Formato DynamoDB
            const name = contractData.M.name?.S || 'Sin nombre';
            const lastVersion = contractData.M.last_version?.S || '0001';

            contractsList.push({
              id: contractId,
              name,
              version: lastVersion,
              version_padded: lastVersion
            });
          } else if (contractData.name) {
            // Formato ya procesado
            const name = contractData.name || 'Sin nombre';
            const lastVersion = contractData.last_version || '0001';

            contractsList.push({
              id: contractId,
              name,
              version: lastVersion,
              version_padded: lastVersion
            });
          }
        });

        setContratos(contractsList.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error('Error al cargar contratos:', error);
      setContratos([]);
    } finally {
      setCargandoContratos(false);
    }
  };

  // Efectos y eventos
  useEffect(() => {
    cargarCategorias();
    cargarContratos();
    cargarFlujos();
  }, []);

  // Cargar formularios cuando cambie la categoría del flujo actual
  useEffect(() => {
    if (flujoActual?.categoria) {
      cargarFormularios(flujoActual.categoria);
    }
  }, [flujoActual?.categoria]);

  useEffect(() => {
    // Añadir listeners globales para el panning
    const handleGlobalMouseMove = (e) => {
      if (isPanning) {
        const newOffset = {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y
        };
        actualizarPanOffset(newOffset);
        e.preventDefault();
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (isPanning) {
        setIsPanning(false);
        e.preventDefault();
      }
    };

    if (isPanning) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isPanning, panStart]);

  useEffect(() => {
    // Cerrar dropdown al hacer clic fuera
    const cerrarDropdown = (e) => {
      if (!e.target.closest('.selector-container')) {
        setMostrarSelectorFlujo(false);
      }
    };

    // Atajos de teclado
    const manejarTeclado = (e) => {
      // Guardar
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (flujoActual) guardarFlujo();
      }

      // no borrar mientras se escribe en inputs/textarea/contentEditable
      const ae = document.activeElement;
      const editing = ae && (
        ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable
      );

      // Suprimir (Delete) para eliminar elemento seleccionado
      if (e.key === 'Delete' && !editing) {
        e.preventDefault();

        // Eliminar bloque seleccionado
        if (bloqueSeleccionado) {
          const bloque = bloques.find(b => b.id === bloqueSeleccionado);
          if (bloque && bloque.tipo !== 'inicio' && bloque.tipo !== 'fin') {
            eliminarBloque(bloqueSeleccionado);
          }
        }

        // Eliminar conexión seleccionada
        if (conexionSeleccionada) {
          eliminarConexion(conexionSeleccionada);
        }
      }

      // Escape para deseleccionar
      if (e.key === 'Escape') {
        setBloqueSeleccionado(null);
        setConexionSeleccionada(null);
        setMostrarSelectorFlujo(false);
      }
    };

    document.addEventListener('click', cerrarDropdown);
    document.addEventListener('keydown', manejarTeclado);

    return () => {
      document.removeEventListener('click', cerrarDropdown);
      document.removeEventListener('keydown', manejarTeclado);
    };
  }, [flujoActual, bloqueSeleccionado, bloques]);

  // Zoom con rueda del ratón centrado en el cursor (event listener no-pasivo)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      const oldZoom = zoomRef.current;
      const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(oldZoom + delta).toFixed(2)));
      if (newZoom === oldZoom) return;

      const rect = canvas.getBoundingClientRect();
      const pan = panOffsetRef.current;
      const worldX = (e.clientX - rect.left - pan.x) / oldZoom;
      const worldY = (e.clientY - rect.top - pan.y) / oldZoom;

      actualizarZoom(newZoom);
      actualizarPanOffset({
        x: e.clientX - rect.left - worldX * newZoom,
        y: e.clientY - rect.top - worldY * newZoom
      });
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVolver = () => {
    navigate('/dashboard');
  };

  // Función para cargar flujo completo con flow_data
  const cargarFlujoCompleto = async (flowId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/flows',
        {
          headers,
          params: {
            flow_id: flowId
          }
        }
      );

      if (response.data) {
        const flujoCompleto = {
          id: response.data.flow_id,
          nombre: response.data.flow_name,
          descripcion: response.data.description || '',
          categoria: response.data.category || '',
          fechaCreacion: response.data.created_at,
          fechaModificacion: response.data.updated_at,
          bloques: response.data.flow_data?.blocks?.map(block => ({
            id: block.id,
            tipo: block.type,
            nombre: block.name,
            posicion: block.position,
            propiedades: block.properties || {}
          })) || [],
          conexiones: response.data.flow_data?.connections?.map(conn => ({
            id: conn.id,
            from: conn.from,
            fromSide: conn.from_side,
            to: conn.to,
            toSide: conn.to_side
          })) || []
        };

        console.log('🔍 Flujo completo cargado:', flujoCompleto);
        return flujoCompleto;
      }
    } catch (error) {
      console.error('Error al cargar flujo completo:', error);
      mostrarMensaje('❌ Error al cargar el flujo completo', 'error');
      throw error;
    }
  };

  const seleccionarFlujo = async (flujo) => {
    if (!flujo) {
      setFlujoActual(null);
      setBloques([]);
      setEdges([]);
      setMostrarSelectorFlujo(false);
      setBloqueSeleccionado(null);
      setModoEdicion(false);
      return;
    }

    try {
      // Mostrar loading
      setFlujoActual({ ...flujo, cargando: true });
      setMostrarSelectorFlujo(false);

      // Cargar flujo completo desde el servidor
      const flujoCompleto = await cargarFlujoCompleto(flujo.id);

      // Actualizar estado con datos completos
      setFlujoActual(flujoCompleto);
      setBloques(flujoCompleto.bloques || []);
      setEdges(flujoCompleto.conexiones || []);
      setBloqueSeleccionado(null);
      setModoEdicion(true);

      console.log('✅ Flujo seleccionado y cargado:', flujoCompleto.nombre);
      mostrarMensaje(`✅ Flujo "${flujoCompleto.nombre}" cargado correctamente`, 'success');

    } catch (error) {
      console.error('Error al seleccionar flujo:', error);
      setFlujoActual(null);
      setBloques([]);
      setEdges([]);
      setModoEdicion(false);
      mostrarMensaje('❌ Error al cargar el flujo seleccionado', 'error');
    }
  };

  // Función para abrir modal de nuevo flujo
  const abrirModalNuevoFlujo = () => {
    setDatosNuevoFlujo({
      nombre: '',
      descripcion: '',
      categoria: ''
    });
    setErroresModalFlujo({});
    setMostrarModalNuevoFlujo(true);
  };

  // Función para validar datos del modal
  const validarDatosModalFlujo = () => {
    const errores = {};

    if (!datosNuevoFlujo.nombre.trim()) {
      errores.nombre = 'El nombre es obligatorio';
    }

    if (!datosNuevoFlujo.categoria) {
      errores.categoria = 'Debes seleccionar una categoría';
    }

    setErroresModalFlujo(errores);
    return Object.keys(errores).length === 0;
  };

  const crearNuevoFlujo = () => {
    abrirModalNuevoFlujo();
  };

  // Función para crear flujo desde el modal
  const crearFlujoDesdeModal = async () => {
    if (!validarDatosModalFlujo()) {
      return;
    }

    setGuardando(true);
    try {
      const nuevoFlujo = await crearFlujo(datosNuevoFlujo);
      setFlujos([...flujos, nuevoFlujo]);
      setMostrarModalNuevoFlujo(false);
      seleccionarFlujo(nuevoFlujo);
      mostrarMensaje(`✅ Flujo "${nuevoFlujo.nombre}" creado exitosamente`);
    } catch (error) {
      console.error('Error al crear flujo:', error);
      mostrarMensaje('❌ Error al crear el flujo. Inténtalo de nuevo.', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const mostrarMensaje = (texto, tipo = 'success') => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 3000);
  };

  const guardarFlujo = async () => {
    if (!flujoActual) return;

    setGuardando(true);

    try {
      const flujoActualizado = {
        ...flujoActual,
        bloques: bloques,
        conexiones: edges
      };

      await actualizarFlujo(flujoActual.id, flujoActualizado, bloques, edges);

      // Actualizar el flujo en la lista local
      const flujoIndex = flujos.findIndex(f => f.id === flujoActual.id);
      if (flujoIndex !== -1) {
        const nuevaListaFlujos = [...flujos];
        nuevaListaFlujos[flujoIndex] = {
          ...flujoActualizado,
          fechaModificacion: new Date().toISOString()
        };
        setFlujos(nuevaListaFlujos);
        setFlujoActual(nuevaListaFlujos[flujoIndex]);
      }

      mostrarMensaje(`✅ Flujo "${flujoActual.nombre}" guardado correctamente`);

    } catch (error) {
      mostrarMensaje('❌ Error al guardar el flujo. Inténtalo de nuevo.', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const iniciarDrag = (e, tipoBloque) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      tipo: tipoBloque.id,
      nombre: tipoBloque.nombre,
      descripcion: tipoBloque.descripcion,
      color: tipoBloque.color,
      icono: tipoBloque.icono,
      propiedades: tipoBloque.propiedades || []
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Organizar bloques por categorías
  const categoriasBloques = {
    'Identificación': ['imei_identifier', 'imei_validation', 'device_identifier', 'select_device_manual'],
    'Conexión y Diagnóstico': ['show_qr', 'diagnosis_screen', 'device_photos'],
    'Ofertas': ['pre_offer', 'offer_screen'],
    'Formularios y Datos': ['form_cosmetic', 'form_functional', 'get_customer_id', 'customer_data'],
    'Documentación': ['aditional_docs'],
    'Finalización': ['contract_signature']
  };

  // Funciones del canvas
  const manejarDrop = (e) => {
    e.preventDefault();
    setDragOverCanvas(false);

    if (!flujoActual) {
      alert('Primero selecciona o crea un flujo');
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / zoom;
    const y = (e.clientY - rect.top - panOffset.y) / zoom;

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      // Validar reglas especiales para formularios
      if (data.tipo === 'form_cosmetic') {
        const existeFormCosmetic = bloques.some(bloque => bloque.tipo === 'form_cosmetic');
        if (existeFormCosmetic) {
          alert('Solo puede haber un bloque de formulario cosmético por flujo.');
          return;
        }
      }

      if (data.tipo === 'form_functional') {
        const existeFormFunctional = bloques.some(bloque => bloque.tipo === 'form_functional');
        if (existeFormFunctional) {
          alert('Solo puede haber un bloque de formulario funcional por flujo.');
          return;
        }
      }

      // Crear nuevo bloque
      const nuevoBloque = {
        id: `bloque_${Date.now()}`,
        tipo: data.tipo,
        nombre: data.nombre,
        posicion: { x: x - 90, y: y - 30 }, // Centrar el bloque
        propiedades: {}
      };

      // Inicializar propiedades especiales con defaults por tipo
      if (data.propiedades && data.propiedades.length > 0) {
        data.propiedades.forEach(prop => {
          if (prop === 'count') {
            nuevoBloque.propiedades[prop] = 1;
          } else if (prop === 'mandatory') {
            nuevoBloque.propiedades[prop] = true;
          } else if (prop === 'types_allowed') {
            nuevoBloque.propiedades[prop] = 'both';
          } else {
            nuevoBloque.propiedades[prop] = '';
          }
        });
      }

      setBloques([...bloques, nuevoBloque]);
      setBloqueSeleccionado(nuevoBloque.id);

    } catch (error) {
      console.error('Error al procesar drop:', error);
    }
  };

  const manejarDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverCanvas(true);
  };

  const manejarDragLeave = (e) => {
    // Solo establecer false si realmente salimos del canvas
    const rect = canvasRef.current.getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) {
      setDragOverCanvas(false);
    }
  };

  const seleccionarBloque = (bloqueId) => {
    setBloqueSeleccionado(bloqueId);
  };

  // Funciones para panning del canvas
  const handleCanvasMouseDown = (e) => {
    // Solo hacer panning si no estamos haciendo click en un bloque o handle
    const target = e.target;
    const isCanvas = target === canvasRef.current ||
      target.classList.contains('canvas-background') ||
      target.tagName === 'svg' ||
      (target.tagName === 'path' && !target.closest('[data-block]'));

    if (isCanvas && !linking && e.button === 0) { // Solo click izquierdo
      setIsPanning(true);
      setPanStart({
        x: e.clientX - panOffset.x,
        y: e.clientY - panOffset.y
      });
      e.preventDefault();
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (isPanning) {
      const newOffset = {
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      };
      actualizarPanOffset(newOffset);
      e.preventDefault();
    }

    // Mantener la funcionalidad existente de linking
    if (linking) {
      const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
      setLinking(prev => ({ ...prev, toPos: { x, y } }));
    }
  };

  const handleCanvasMouseUp = (e) => {
    if (isPanning) {
      setIsPanning(false);
      e.preventDefault();
    }

    // Mantener la funcionalidad existente de linking
    if (linking) {
      setLinking(null);
    }
  };

  // Función para convertir coordenadas del canvas teniendo en cuenta el pan offset y zoom
  const getCanvasCoordinates = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panOffset.x) / zoom,
      y: (clientY - rect.top - panOffset.y) / zoom
    };
  };

  const moverBloque = (bloqueId, nuevaPosicion) => {
    const bloqueIndex = bloques.findIndex(b => b.id === bloqueId);
    if (bloqueIndex === -1) return;

    // Snap a la cuadrícula (grid de 20px)
    const snapSize = 20;
    const snappedX = Math.round(nuevaPosicion.x / snapSize) * snapSize;
    const snappedY = Math.round(nuevaPosicion.y / snapSize) * snapSize;

    // Límites del mundo canvas (fijos, independientes de la resolución del monitor)
    const minWidth = 180;
    const minHeight = 80;
    const maxX = CANVAS_WORLD_WIDTH - minWidth;
    const maxY = CANVAS_WORLD_HEIGHT - minHeight;

    const finalX = Math.max(0, Math.min(snappedX, maxX));
    const finalY = Math.max(0, Math.min(snappedY, maxY));

    const nuevaListaBloques = [...bloques];
    nuevaListaBloques[bloqueIndex] = {
      ...nuevaListaBloques[bloqueIndex],
      posicion: { x: finalX, y: finalY }
    };

    setBloques(nuevaListaBloques);
  };

  const eliminarBloque = (bloqueId) => {
    // No permitir eliminar bloques de inicio y fin
    const bloque = bloques.find(b => b.id === bloqueId);
    if (bloque && (bloque.tipo === 'inicio' || bloque.tipo === 'fin')) {
      return;
    }

    setBloques(bloques.filter(b => b.id !== bloqueId));
    setEdges(edges.filter(e => e.from !== bloqueId && e.to !== bloqueId));
    if (bloqueSeleccionado === bloqueId) {
      setBloqueSeleccionado(null);
    }
    setContextMenu({ visible: false, x: 0, y: 0, targetId: null });
  };

  // Utilidades para anclas y conexiones
  // Utiliza siempre coords del "mundo canvas" (sin pan aplicado)
  const getAnchor = (blockId, side = 'right') => {
    const el = blockRefs.current[blockId];
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!el || !canvasRect) return { x: 0, y: 0 };

    const r = el.getBoundingClientRect();

    // Posición del anchor en coords de pantalla relativas al canvas…
    const xCanvas = side === 'right'
      ? (r.right - canvasRect.left)
      : (r.left - canvasRect.left);
    const yCanvas = (r.top - canvasRect.top) + r.height / 2;

    // …llevamos a coords del mundo: restamos panOffset y dividimos por zoom
    return {
      x: (xCanvas - panOffset.x) / zoom,
      y: (yCanvas - panOffset.y) / zoom
    };
  };

  const addEdge = (fromId, fromSide, toId, toSide) => {
    if (!fromId || !toId) return;

    if (fromId === toId) {
      mostrarMensaje('❌ Un bloque no puede conectarse a sí mismo', 'error');
      return;
    }

    // reglas básicas: inicio no recibe, fin no emite
    const fromBlock = bloques.find(b => b.id === fromId);
    const toBlock = bloques.find(b => b.id === toId);
    if (fromBlock?.tipo === 'fin') {
      mostrarMensaje('❌ El bloque de fin no puede tener conexiones de salida', 'error');
      return;
    }
    if (toBlock?.tipo === 'inicio') {
      mostrarMensaje('❌ El bloque de inicio no puede tener conexiones de entrada', 'error');
      return;
    }

    // Evitar conexiones duplicadas
    const existeConexion = edges.some(edge =>
      edge.from === fromId && edge.fromSide === fromSide &&
      edge.to === toId && edge.toSide === toSide
    );
    if (existeConexion) {
      mostrarMensaje('❌ Esta conexión ya existe', 'error');
      return;
    }

    // NUEVAS RESTRICCIONES:

    // 1. Un bloque solo puede tener UNA conexión de salida (output)
    const tieneConexionSalida = edges.some(edge => edge.from === fromId);
    if (tieneConexionSalida) {
      console.log(`❌ El bloque ${fromId} ya tiene una conexión de salida`);
      mostrarMensaje('❌ Este bloque ya tiene una conexión de salida', 'error');
      return;
    }

    // 2. Un bloque solo puede tener UNA conexión de entrada (input)
    const tieneConexionEntrada = edges.some(edge => edge.to === toId);
    if (tieneConexionEntrada) {
      console.log(`❌ El bloque ${toId} ya tiene una conexión de entrada`);
      mostrarMensaje('❌ Este bloque ya tiene una conexión de entrada', 'error');
      return;
    }

    // 3. Verificar que no se conecte el mismo bloque a múltiples bloques
    const yaEstaConectadoA = edges.some(edge => edge.from === fromId && edge.to !== toId);
    if (yaEstaConectadoA) {
      console.log(`❌ El bloque ${fromId} ya está conectado a otro bloque`);
      mostrarMensaje('❌ Este bloque ya está conectado a otro bloque', 'error');
      return;
    }

    const id = `e_${fromId}_${fromSide}_${toId}_${toSide}_${Date.now()}`;
    setEdges(prev => [...prev, { id, from: fromId, fromSide, to: toId, toSide }]);
    console.log(`✅ Conexión creada: ${fromId} → ${toId}`);

    // Obtener nombres de los bloques para el mensaje
    const fromBlockName = fromBlock?.nombre || fromId;
    const toBlockName = toBlock?.nombre || toId;
    mostrarMensaje(`✅ Conexión creada: ${fromBlockName} → ${toBlockName}`, 'success');
  };

  // curva suave en función del lado de cada extremo
  const pathD = (a, b, fromSide, toSide) => {
    const c1x = a.x + (fromSide === 'right' ? 60 : -60);
    const c2x = b.x + (toSide === 'left' ? -60 : 60);
    return `M ${a.x},${a.y} C ${c1x},${a.y} ${c2x},${b.y} ${b.x},${b.y}`;
  };

  const eliminarConexion = (conexionId) => {
    setEdges(edges.filter(e => e.id !== conexionId));
    if (conexionSeleccionada === conexionId) {
      setConexionSeleccionada(null);
    }
  };

  const duplicarBloque = (bloqueId) => {
    const orig = bloques.find(b => b.id === bloqueId);
    if (!orig) return;
    const copy = {
      ...orig,
      id: `bloque_${Date.now()}`,
      posicion: { x: orig.posicion.x + 24, y: orig.posicion.y + 24 }
    };
    setBloques([...bloques, copy]);
    setContextMenu({ visible: false, x: 0, y: 0, targetId: null });
    setBloqueSeleccionado(copy.id);
  };

  // Función para confirmar eliminación de flujo
  const confirmarEliminacionFlujo = (flujo) => {
    setFlujoAEliminar(flujo);
    setMostrarModalEliminacion(true);
  };

  // Función para eliminar flujo después de confirmación
  const eliminarFlujoConfirmado = async () => {
    if (!flujoAEliminar) return;

    setGuardando(true);
    try {
      await eliminarFlujoBackend(flujoAEliminar.id);

      // Remover flujo de la lista local
      const nuevaListaFlujos = flujos.filter(f => f.id !== flujoAEliminar.id);
      setFlujos(nuevaListaFlujos);

      // Si el flujo eliminado es el actual, deseleccionarlo
      if (flujoActual && flujoActual.id === flujoAEliminar.id) {
        setFlujoActual(null);
        setBloques([]);
        setEdges([]);
        setBloqueSeleccionado(null);
      }

      mostrarMensaje(`✅ Flujo "${flujoAEliminar.nombre}" eliminado correctamente`);
    } catch (error) {
      console.error('Error al eliminar flujo:', error);
      mostrarMensaje('❌ Error al eliminar el flujo. Inténtalo de nuevo.', 'error');
    } finally {
      setGuardando(false);
      setMostrarModalEliminacion(false);
      setFlujoAEliminar(null);
    }
  };

  // Componente para un bloque en el canvas
  const BloqueCanvas = ({ bloque }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const tipoBloque = tiposBloques[bloque.tipo];
    const isSeleccionado = bloqueSeleccionado === bloque.id;

    // Estilos especiales para bloques de inicio y fin
    let estiloEspecial = {};
    if (bloque.tipo === 'inicio') {
      estiloEspecial = {
        backgroundColor: '#10B981',
        color: brandIdentity.colors.white,
        borderColor: '#059669'
      };
    } else if (bloque.tipo === 'fin') {
      estiloEspecial = {
        backgroundColor: '#EF4444',
        color: brandIdentity.colors.white,
        borderColor: '#DC2626'
      };
    }

    const manejarMouseDown = (e) => {
      if (e.button !== 0) return; // solo click izquierdo
      e.stopPropagation();

      const canvasRect = canvasRef.current.getBoundingClientRect();
      const pan = panOffsetRef.current;

      // offset del cursor dentro del bloque en coords de pantalla (considerando pan y zoom)
      const offsetX = e.clientX - canvasRect.left - (bloque.posicion.x * zoom + pan.x);
      const offsetY = e.clientY - canvasRect.top - (bloque.posicion.y * zoom + pan.y);

      setIsDragging(true);
      seleccionarBloque(bloque.id);

      const manejarMouseMove = (ev) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const currentPan = panOffsetRef.current;
        const newX = (ev.clientX - rect.left - currentPan.x - offsetX) / zoom;
        const newY = (ev.clientY - rect.top - currentPan.y - offsetY) / zoom;
        moverBloque(bloque.id, { x: newX, y: newY });
      };

      const manejarMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', manejarMouseMove);
        document.removeEventListener('mouseup', manejarMouseUp);
      };

      document.addEventListener('mousemove', manejarMouseMove);
      document.addEventListener('mouseup', manejarMouseUp);
    };

    const manejarDoubleClick = () => {
      if (bloque.tipo !== 'inicio' && bloque.tipo !== 'fin') {
        const nuevoNombre = prompt('Ingresa el nuevo nombre para este bloque:', bloque.nombre);
        if (nuevoNombre && nuevoNombre.trim()) {
          const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
          const nuevaListaBloques = [...bloques];
          nuevaListaBloques[bloqueIndex] = {
            ...nuevaListaBloques[bloqueIndex],
            nombre: nuevoNombre.trim()
          };
          setBloques(nuevaListaBloques);
        }
      }
    };

    return (
      <div
        ref={(el) => { if (el) blockRefs.current[bloque.id] = el; }}
        style={{
          ...styles.bloqueCanvas,
          ...estiloEspecial,
          ...(isSeleccionado ? styles.bloqueCanvasSeleccionado : {}),
          left: bloque.posicion.x,
          top: bloque.posicion.y,
          cursor: isDragging ? 'grabbing' : 'grab',
          zIndex: isSeleccionado ? 1000 : 1
        }}
        onMouseDown={manejarMouseDown}
        onDoubleClick={manejarDoubleClick}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const rect = canvasRef.current.getBoundingClientRect();
          setContextMenu({
            visible: true,
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            targetId: bloque.id
          });
        }}
        onMouseUp={(e) => {
          if (!linking || linking.fromId === bloque.id || bloque.tipo === 'inicio') return;
          // decide a qué lado del bloque cayó el mouse
          const rect = blockRefs.current[bloque.id].getBoundingClientRect();
          const canvasRect = canvasRef.current.getBoundingClientRect();
          const xLocal = (e.clientX - rect.left);
          const toSide = xLocal < rect.width / 2 ? 'left' : 'right';
          addEdge(linking.fromId, linking.fromSide, bloque.id, toSide);
          setLinking(null);
        }}
        title={tipoBloque ? tipoBloque.descripcion : bloque.tipo}
      >
        {/* Icono */}
        <div style={{
          ...styles.bloqueCanvasIcono,
          color: estiloEspecial.color || brandIdentity.colors.textDark
        }}>
          {bloque.tipo === 'inicio' ? '🚀' :
            bloque.tipo === 'fin' ? '🏁' :
              (tipoBloque ? tipoBloque.icono : '⚙️')}
        </div>

        {/* Nombre del bloque */}
        <div style={{
          ...styles.bloqueCanvasNombre,
          color: estiloEspecial.color || brandIdentity.colors.textDark
        }}>
          {bloque.nombre}
        </div>

        {/* Tipo de bloque */}
        <div style={{
          ...styles.bloqueCanvasTipo,
          color: estiloEspecial.color ? 'rgba(255,255,255,0.8)' : brandIdentity.colors.textLight
        }}>
          {bloque.tipo === 'inicio' ? 'Inicio del flujo' :
            bloque.tipo === 'fin' ? 'Fin del flujo' :
              (tipoBloque ? tipoBloque.nombre : bloque.tipo)}
        </div>

        {/* Indicador de propiedades configurables */}
        {tipoBloque && tipoBloque.propiedades && tipoBloque.propiedades.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: tipoBloque.color,
            color: brandIdentity.colors.white,
            fontSize: '12px',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold'
          }}>
            ⚙
          </div>
        )}

        {/* Botón de eliminar para bloques no fijos */}
        {bloque.tipo !== 'inicio' && bloque.tipo !== 'fin' && isSeleccionado && (
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#EF4444',
              color: brandIdentity.colors.white,
              fontSize: '12px',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            onClick={(e) => {
              e.stopPropagation();
              eliminarBloque(bloque.id);
            }}
            title="Eliminar bloque"
          >
            ×
          </div>
        )}

        {/* ENTRADA/SALIDA IZQUIERDA (híbrido). Ocúltalo sólo si es 'inicio' para entrada */}
        {(true) && (() => {
          // Verificar si este bloque ya tiene conexión de entrada (para input)
          const yaRecibe = edges.some(edge => edge.to === bloque.id);
          // Verificar si este bloque ya tiene conexión de salida (para output)
          const yaEmite = edges.some(edge => edge.from === bloque.id);

          // Para el handle izquierdo: puede ser input O output
          const puedeSerInput = bloque.tipo !== 'inicio' && !yaRecibe;
          const puedeSerOutput = bloque.tipo !== 'fin' && !yaEmite;
          const puedeUsarse = puedeSerInput || puedeSerOutput;

          const esInputInvalido = linking && linking.fromId !== bloque.id && !puedeSerInput;
          const esOutputInvalido = !linking && !puedeSerOutput;

          return (
            <div
              style={{
                ...styles.handles.in,
                ...(linking && linking.fromId !== bloque.id && bloque.tipo !== 'inicio' && handleHover === `${bloque.id}_left` && puedeSerInput ? {
                  boxShadow: `0 0 0 3px ${brandIdentity.colors.primary}40`,
                  borderColor: brandIdentity.colors.primary
                } : {}),
                // Estilo para handle deshabilitado
                ...(!puedeUsarse ? {
                  backgroundColor: '#F3F4F6',
                  borderColor: '#D1D5DB',
                  cursor: 'not-allowed',
                  opacity: 0.5
                } : {}),
                // Estilo para conexión inválida
                ...(esInputInvalido ? {
                  backgroundColor: '#FEE2E2',
                  borderColor: '#F87171',
                  boxShadow: `0 0 0 2px #FCA5A520`
                } : {})
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                // empezar conexión desde el lado izquierdo (output)
                if (!puedeSerOutput) return;
                setLinking({ fromId: bloque.id, fromSide: 'left', toPos: getAnchor(bloque.id, 'left') });
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                // terminar conexión sobre este mismo handle (input)
                if (linking && linking.fromId !== bloque.id && puedeSerInput) {
                  addEdge(linking.fromId, linking.fromSide, bloque.id, 'left');
                  setLinking(null);
                }
              }}
              onMouseEnter={() => {
                if (linking && linking.fromId !== bloque.id && puedeSerInput) {
                  setHandleHover(`${bloque.id}_left`);
                }
              }}
              onMouseLeave={() => {
                setHandleHover(null);
              }}
              title={
                !puedeUsarse ? 'Este nodo ya tiene una conexión' :
                  esInputInvalido ? 'Este bloque ya recibe una conexión' :
                    'Handle de conexión'
              }
            />
          );
        })()}

        {/* ENTRADA/SALIDA DERECHA (híbrido). Ocúltalo sólo si es 'fin' para salida */}
        {(true) && (() => {
          // Verificar si este bloque ya tiene conexión de entrada (para input)
          const yaRecibe = edges.some(edge => edge.to === bloque.id);
          // Verificar si este bloque ya tiene conexión de salida (para output)
          const yaEmite = edges.some(edge => edge.from === bloque.id);

          // Para el handle derecho: puede ser input O output
          const puedeSerInput = bloque.tipo !== 'inicio' && !yaRecibe;
          const puedeSerOutput = bloque.tipo !== 'fin' && !yaEmite;
          const puedeUsarse = puedeSerInput || puedeSerOutput;

          const esInputInvalido = linking && linking.fromId !== bloque.id && !puedeSerInput;
          const esOutputInvalido = !linking && !puedeSerOutput;

          return (
            <div
              style={{
                ...styles.handles.out,
                ...(linking && linking.fromId !== bloque.id && bloque.tipo !== 'inicio' && handleHover === `${bloque.id}_right` && puedeSerInput ? {
                  boxShadow: `0 0 0 3px ${brandIdentity.colors.primary}40`,
                  borderColor: brandIdentity.colors.primary
                } : {}),
                // Estilo para handle deshabilitado
                ...(!puedeUsarse ? {
                  backgroundColor: '#F3F4F6',
                  borderColor: '#D1D5DB',
                  cursor: 'not-allowed',
                  opacity: 0.5
                } : {}),
                // Estilo para conexión inválida
                ...(esInputInvalido ? {
                  backgroundColor: '#FEE2E2',
                  borderColor: '#F87171',
                  boxShadow: `0 0 0 2px #FCA5A520`
                } : {})
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                // empezar conexión desde el lado derecho (output)
                if (!puedeSerOutput) return;
                setLinking({ fromId: bloque.id, fromSide: 'right', toPos: getAnchor(bloque.id, 'right') });
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                // terminar conexión sobre este mismo handle (input)
                if (linking && linking.fromId !== bloque.id && puedeSerInput) {
                  addEdge(linking.fromId, linking.fromSide, bloque.id, 'right');
                  setLinking(null);
                }
              }}
              onMouseEnter={() => {
                if (linking && linking.fromId !== bloque.id && puedeSerInput) {
                  setHandleHover(`${bloque.id}_right`);
                }
              }}
              onMouseLeave={() => {
                setHandleHover(null);
              }}
              title={
                !puedeUsarse ? 'Este nodo ya tiene una conexión' :
                  esInputInvalido ? 'Este bloque ya recibe una conexión' :
                    'Handle de conexión'
              }
            />
          );
        })()}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: brandIdentity.spacing.md }}>
          <Logo />
          <h1 style={styles.title}>Gestión de Flujos</h1>
        </div>

        <div style={styles.selectorFlujos}>
          {/* Selector de Flujos */}
          <div style={styles.selectorContainer} className="selector-container">
            <button
              style={styles.selectorButton}
              onClick={() => setMostrarSelectorFlujo(!mostrarSelectorFlujo)}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#F3F4F6';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = brandIdentity.colors.white;
              }}
            >
              <span>
                {flujoActual?.cargando ? '⏳ Cargando flujo...' :
                  flujoActual ? flujoActual.nombre : 'Seleccionar flujo'}
              </span>
              <span style={{ fontSize: '12px' }}>▼</span>
            </button>

            {mostrarSelectorFlujo && (
              <div style={styles.dropdown}>
                <div
                  style={{ ...styles.dropdownItem, backgroundColor: '#F3F4F6', fontWeight: brandIdentity.typography.weights.semibold }}
                >
                  📋 Flujos Existentes
                </div>
                {cargandoFlujos ? (
                  <div style={{ ...styles.dropdownItem, textAlign: 'center', color: brandIdentity.colors.textLight }}>
                    ⏳ Cargando flujos...
                  </div>
                ) : errorFlujos ? (
                  <div style={{ ...styles.dropdownItem, textAlign: 'center', color: '#EF4444' }}>
                    ❌ Error al cargar flujos
                    <button
                      type="button"
                      style={{
                        marginLeft: '8px',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        fontSize: 'inherit'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        cargarFlujos();
                      }}
                    >
                      Reintentar
                    </button>
                  </div>
                ) : flujos.length === 0 ? (
                  <div style={{ ...styles.dropdownItem, textAlign: 'center', color: brandIdentity.colors.textLight }}>
                    📭 No hay flujos disponibles
                  </div>
                ) : (
                  flujos.map((flujo) => (
                    <div
                      key={flujo.id}
                      style={{
                        ...styles.dropdownItem,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}
                    >
                      <div
                        style={{ flex: 1, cursor: 'pointer' }}
                        onClick={() => seleccionarFlujo(flujo)}
                      >
                        <div style={{ fontWeight: brandIdentity.typography.weights.medium }}>
                          {flujo.nombre}
                        </div>
                        <div style={{
                          fontSize: brandIdentity.typography.sizes.small,
                          color: brandIdentity.colors.textLight,
                          marginTop: '2px'
                        }}>
                          {flujo.descripcion}
                        </div>
                        <div style={{
                          fontSize: brandIdentity.typography.sizes.xs,
                          color: brandIdentity.colors.textLight,
                          marginTop: '2px'
                        }}>
                          {(flujo.bloques?.length || 0) - 2} bloques • Modificado: {new Date(flujo.fechaModificacion).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmarEliminacionFlujo(flujo);
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#EF4444',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '4px',
                          borderRadius: '4px',
                          marginLeft: '8px',
                          flexShrink: 0
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#FEE2E2';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                        title="Eliminar flujo"
                      >
                        🗑️
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Botón Nuevo Flujo */}
          <button
            style={styles.nuevoFlujoButton}
            onClick={crearNuevoFlujo}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#059669';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = brandIdentity.colors.secondary;
            }}
          >
            + Nuevo Flujo
          </button>

          {/* Botón Guardar */}
          {flujoActual && (
            <button
              style={{
                ...styles.backButton,
                backgroundColor: guardando ? '#9CA3AF' : brandIdentity.colors.accent
              }}
              onClick={guardarFlujo}
              disabled={guardando}
            >
              {guardando ? '⏳ Guardando...' : '💾 Guardar'}
            </button>
          )}

          {/* Botón Volver */}
          <button
            style={styles.backButton}
            onClick={handleVolver}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = brandIdentity.colors.primaryDark;
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = brandIdentity.colors.primary;
            }}
          >
            ← Volver al Dashboard
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Panel de Herramientas */}
        <div style={styles.sidebar}>
          <h3 style={styles.sectionTitle}>🧩 Bloques de Flujo</h3>

          {Object.entries(categoriasBloques).map(([categoria, tiposBloqueIds]) => (
            <div key={categoria} style={styles.categoriaHerramientas}>
              <div style={styles.categoriaTitulo}>
                {categoria}
              </div>

              {tiposBloqueIds.map(tipoId => {
                const tipoBloque = tiposBloques[tipoId];
                return (
                  <div
                    key={tipoId}
                    style={styles.bloqueHerramienta}
                    draggable
                    onDragStart={(e) => iniciarDrag(e, tipoBloque)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = tipoBloque.color;
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = `0 4px 12px rgba(0, 0, 0, 0.15)`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = '#E5E7EB';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.cursor = 'grabbing';
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.cursor = 'grab';
                    }}
                  >
                    <div style={styles.bloqueIcono}>
                      {tipoBloque.icono}
                    </div>

                    <div style={styles.bloqueNombre}>
                      {tipoBloque.nombre}
                    </div>

                    <div style={styles.bloqueDescripcion} title={tipoBloque.descripcion}>
                      {tipoBloque.descripcion}
                    </div>

                    {/* Indicador si tiene propiedades especiales */}
                    {tipoBloque.propiedades && tipoBloque.propiedades.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: tipoBloque.color,
                        color: brandIdentity.colors.white,
                        fontSize: '10px',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}>
                        ⚙
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div style={{
            backgroundColor: '#F3F4F6',
            padding: brandIdentity.spacing.sm,
            borderRadius: '8px',
            marginTop: brandIdentity.spacing.md,
            fontSize: brandIdentity.typography.sizes.xs,
            color: brandIdentity.colors.textLight
          }}>
            <div style={{ textAlign: 'center', marginBottom: brandIdentity.spacing.sm }}>
              💡 <strong>Tips de uso:</strong>
            </div>
            <div style={{ lineHeight: '1.4' }}>
              • Arrastra bloques al canvas<br />
              • <strong>Arrastra el canvas</strong>: Navegar<br />
              • <strong>Rueda del ratón</strong>: Zoom<br />
              • <strong>+ / −</strong>: Botones de zoom (inf. derecha)<br />
              • Doble clic para renombrar<br />
              • <strong>Ctrl/Cmd + S</strong>: Guardar<br />
              • <strong>Supr</strong>: Eliminar seleccionado<br />
              • <strong>Esc</strong>: Deseleccionar<br />
              • <strong>Clic derecho</strong>: Menú contextual<br />
            </div>
          </div>
        </div>

        {/* Canvas Principal */}
        <div
          style={{
            ...styles.canvas,
            ...(dragOverCanvas ? {
              backgroundColor: 'rgba(99, 102, 241, 0.05)',
              borderColor: brandIdentity.colors.primary,
              border: `2px dashed ${brandIdentity.colors.primary}`
            } : {}),
            cursor: isPanning ? 'grabbing' : linking ? 'crosshair' : 'grab',
            userSelect: 'none'
          }}
          ref={canvasRef}
          onDrop={manejarDrop}
          onDragOver={manejarDragOver}
          onDragLeave={manejarDragLeave}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onClick={(e) => {
            // Solo deseleccionar si no estamos haciendo panning
            if (!isPanning) {
              setBloqueSeleccionado(null);
              setConexionSeleccionada(null);
              setContextMenu({ visible: false, x: 0, y: 0, targetId: null });
            }
          }}
        >
          {/* Contenedor con transformación para panning + zoom (tamaño fijo del mundo) */}
          <div
            className="canvas-background"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${CANVAS_WORLD_WIDTH}px`,
              height: `${CANVAS_WORLD_HEIGHT}px`,
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              transition: isPanning ? 'none' : 'transform 0.1s ease-out'
            }}
          >
            {/* Conexiones (SVG overlay) */}
            <svg style={styles.edgeSvg}>
              <defs>
                <marker id="arrow" viewBox="0 0 10 10" refX="10" refY="5"
                  markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#9CA3AF" />
                </marker>
                <marker id="arrow-selected" viewBox="0 0 10 10" refX="10" refY="5"
                  markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366F1" />
                </marker>
              </defs>

              {edges.map(edge => {
                const a = getAnchor(edge.from, edge.fromSide);
                const b = getAnchor(edge.to, edge.toSide);
                const d = pathD(a, b, edge.fromSide, edge.toSide);
                const isSelected = conexionSeleccionada === edge.id;

                return (
                  <g key={edge.id}>
                    {/* Línea invisible más gruesa para detectar clics */}
                    <path
                      d={d}
                      stroke="transparent"
                      strokeWidth="12"
                      fill="none"
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBloqueSeleccionado(null);
                        setConexionSeleccionada(edge.id);
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.nextSibling.setAttribute('stroke', '#6366F1');
                          e.target.nextSibling.setAttribute('stroke-width', '3');
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.nextSibling.setAttribute('stroke', '#9CA3AF');
                          e.target.nextSibling.setAttribute('stroke-width', '2');
                        }
                      }}
                    />

                    {/* Línea visible */}
                    <path
                      d={d}
                      stroke={isSelected ? "#6366F1" : "#9CA3AF"}
                      strokeWidth={isSelected ? "3" : "2"}
                      fill="none"
                      markerEnd={isSelected ? "url(#arrow-selected)" : "url(#arrow)"}
                      style={{ pointerEvents: 'none' }}
                    />
                  </g>
                );
              })}

              {linking && (() => {
                const a = getAnchor(linking.fromId, linking.fromSide);
                const b = linking.toPos || a;
                // estimación de toSide para la vista previa (si el mouse está a la derecha del origen, curvamos hacia 'left', y viceversa)
                const guessToSide = b.x >= a.x ? 'left' : 'right';
                const d = pathD(a, b, linking.fromSide, guessToSide);
                return <path d={d} stroke="#A3A3A3" strokeDasharray="6 4" strokeWidth="2" fill="none" />;
              })()}
            </svg>

            {/* Botón X para conexiones seleccionadas */}
            {conexionSeleccionada && (() => {
              const edge = edges.find(e => e.id === conexionSeleccionada);
              if (!edge) return null;

              const a = getAnchor(edge.from, edge.fromSide);
              const b = getAnchor(edge.to, edge.toSide);

              // Calcular punto medio de la curva bezier
              const midX = (a.x + b.x) / 2;
              const midY = (a.y + b.y) / 2;

              return (
                <div
                  style={{
                    position: 'absolute',
                    left: midX - 10,
                    top: midY - 10,
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#EF4444',
                    color: brandIdentity.colors.white,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    border: `2px solid ${brandIdentity.colors.white}`,
                    zIndex: 1500
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    eliminarConexion(conexionSeleccionada);
                  }}
                  title="Eliminar conexión"
                >
                  ×
                </div>
              );
            })()}

            {/* Renderizar bloques del flujo actual */}
            {bloques.map((bloque) => (
              <BloqueCanvas key={bloque.id} bloque={bloque} />
            ))}

            {/* Estado vacío cuando no hay flujo seleccionado o no hay bloques */}
            {!flujoActual ? (
              <div style={styles.canvasVacio}>
                <p>📋 Selecciona un flujo o crea uno nuevo</p>
                <p style={{ fontSize: brandIdentity.typography.sizes.body, marginTop: brandIdentity.spacing.sm }}>
                  Usa el selector de flujos en la parte superior
                </p>
              </div>
            ) : bloques.length === 0 ? (
              <div style={styles.canvasVacio}>
                <p>🚀 Arrastra bloques aquí para crear tu flujo</p>
                <p style={{ fontSize: brandIdentity.typography.sizes.body, marginTop: brandIdentity.spacing.sm }}>
                  Los bloques de <strong>Inicio</strong> y <strong>Fin</strong> se agregaron automáticamente
                </p>
              </div>
            ) : null}

            {/* Menú contextual */}
            {contextMenu.visible && (
              <div
                style={{
                  position: 'absolute',
                  left: contextMenu.x,
                  top: contextMenu.y,
                  background: brandIdentity.colors.white,
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 2000,
                  overflow: 'hidden',
                  minWidth: 180
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                  onClick={() => duplicarBloque(contextMenu.targetId)}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = brandIdentity.colors.white}
                >📄 Duplicar</div>

                <div
                  style={{ padding: '8px 12px', cursor: 'pointer', color: '#B91C1C' }}
                  onClick={() => eliminarBloque(contextMenu.targetId)}
                  onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={e => e.currentTarget.style.background = brandIdentity.colors.white}
                >🗑️ Eliminar</div>
              </div>
            )}
          </div> {/* Fin del contenedor de panning */}

          {/* Controles de navegación del canvas */}
          {(panOffset.x !== 0 || panOffset.y !== 0) && (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: brandIdentity.typography.sizes.xs
            }}>
              <span style={{ color: brandIdentity.colors.textLight }}>
                📍 x: {Math.round(panOffset.x)}, y: {Math.round(panOffset.y)}
              </span>
              <button
                onClick={() => actualizarPanOffset({ x: 0, y: 0 })}
                style={{
                  backgroundColor: brandIdentity.colors.primary,
                  color: brandIdentity.colors.white,
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: brandIdentity.typography.sizes.xs,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                title="Centrar vista"
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = brandIdentity.colors.primaryDark;
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = brandIdentity.colors.primary;
                }}
              >
                🎯 Centrar
              </button>
            </div>
          )}

          {/* Indicador de panning activo */}
          {isPanning && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              backgroundColor: 'rgba(99, 102, 241, 0.9)',
              color: brandIdentity.colors.white,
              border: 'none',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: brandIdentity.typography.sizes.small,
              fontWeight: brandIdentity.typography.weights.medium,
              zIndex: 1000,
              pointerEvents: 'none'
            }}>
              🖱️ Arrastrando vista...
            </div>
          )}

          {/* Controles de Zoom */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '6px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            userSelect: 'none'
          }}>
            <button
              onClick={() => actualizarZoom(+(Math.max(ZOOM_MIN, zoom - ZOOM_STEP)).toFixed(2))}
              style={{
                width: '28px', height: '28px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                backgroundColor: brandIdentity.colors.white,
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1
              }}
              title="Alejar"
            >−</button>

            <span style={{
              minWidth: '44px',
              textAlign: 'center',
              fontSize: brandIdentity.typography.sizes.small,
              color: brandIdentity.colors.textDark,
              fontWeight: brandIdentity.typography.weights.medium
            }}>
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={() => actualizarZoom(+(Math.min(ZOOM_MAX, zoom + ZOOM_STEP)).toFixed(2))}
              style={{
                width: '28px', height: '28px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                backgroundColor: brandIdentity.colors.white,
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1
              }}
              title="Acercar"
            >+</button>

            <button
              onClick={() => actualizarZoom(1.0)}
              style={{
                padding: '4px 8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                backgroundColor: brandIdentity.colors.white,
                cursor: 'pointer',
                fontSize: brandIdentity.typography.sizes.xs,
                color: brandIdentity.colors.textLight
              }}
              title="Restablecer zoom"
            >1:1</button>
          </div>
        </div>

        {/* Panel de Propiedades */}
        <div style={styles.propertiesPanel}>
          <h3 style={styles.sectionTitle}>⚙️ Propiedades</h3>

          {bloqueSeleccionado ? (() => {
            const bloque = bloques.find(b => b.id === bloqueSeleccionado);
            if (!bloque) return null;

            const tipoBloque = tiposBloques[bloque.tipo];
            const tienePropiedades = tipoBloque && tipoBloque.propiedades && tipoBloque.propiedades.length > 0;

            return (
              <div>
                {/* Información del bloque */}
                <div style={{
                  backgroundColor: '#F9FAFB',
                  padding: brandIdentity.spacing.md,
                  borderRadius: '8px',
                  marginBottom: brandIdentity.spacing.md
                }}>
                  <div style={{
                    fontSize: brandIdentity.typography.sizes.body,
                    fontWeight: brandIdentity.typography.weights.semibold,
                    color: brandIdentity.colors.textDark,
                    marginBottom: brandIdentity.spacing.xs
                  }}>
                    {bloque.nombre}
                  </div>

                  <div style={{
                    fontSize: brandIdentity.typography.sizes.small,
                    color: brandIdentity.colors.textLight,
                    marginBottom: brandIdentity.spacing.xs
                  }}>
                    Tipo: {tipoBloque ? tipoBloque.nombre : bloque.tipo}
                  </div>

                  {tipoBloque && (
                    <div style={{
                      fontSize: brandIdentity.typography.sizes.xs,
                      color: brandIdentity.colors.textLight,
                      lineHeight: '1.4'
                    }}>
                      {tipoBloque.descripcion}
                    </div>
                  )}
                </div>

                {/* Propiedades básicas */}
                <div style={{ marginBottom: brandIdentity.spacing.lg }}>
                  <div style={{
                    fontSize: brandIdentity.typography.sizes.small,
                    fontWeight: brandIdentity.typography.weights.semibold,
                    color: brandIdentity.colors.textDark,
                    marginBottom: brandIdentity.spacing.sm
                  }}>
                    Propiedades Básicas
                  </div>

                  {/* Nombre personalizado (solo para bloques no fijos) */}
                  {bloque.tipo !== 'inicio' && bloque.tipo !== 'fin' && (
                    <div style={{ marginBottom: brandIdentity.spacing.sm }}>
                      <label style={{
                        fontSize: brandIdentity.typography.sizes.small,
                        color: brandIdentity.colors.textDark,
                        display: 'block',
                        marginBottom: brandIdentity.spacing.xs
                      }}>
                        Nombre del bloque:
                      </label>
                      <input
                        type="text"
                        value={bloque.nombre}
                        onChange={(e) => {
                          const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                          const nuevaListaBloques = [...bloques];
                          nuevaListaBloques[bloqueIndex] = {
                            ...nuevaListaBloques[bloqueIndex],
                            nombre: e.target.value
                          };
                          setBloques(nuevaListaBloques);
                        }}
                        style={{
                          width: '100%',
                          padding: brandIdentity.spacing.sm,
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: brandIdentity.typography.sizes.small,
                          fontFamily: brandIdentity.typography.fontFamily
                        }}
                      />
                    </div>
                  )}

                  {/* Posición */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: brandIdentity.spacing.sm,
                    marginBottom: brandIdentity.spacing.sm
                  }}>
                    <div>
                      <label style={{
                        fontSize: brandIdentity.typography.sizes.xs,
                        color: brandIdentity.colors.textLight,
                        display: 'block',
                        marginBottom: '2px'
                      }}>
                        Posición X:
                      </label>
                      <input
                        type="number"
                        value={Math.round(bloque.posicion.x)}
                        onChange={(e) => {
                          const nuevaPosicion = {
                            ...bloque.posicion,
                            x: parseInt(e.target.value) || 0
                          };
                          moverBloque(bloque.id, nuevaPosicion);
                        }}
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: brandIdentity.typography.sizes.xs,
                          fontFamily: brandIdentity.typography.fontFamily
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        fontSize: brandIdentity.typography.sizes.xs,
                        color: brandIdentity.colors.textLight,
                        display: 'block',
                        marginBottom: '2px'
                      }}>
                        Posición Y:
                      </label>
                      <input
                        type="number"
                        value={Math.round(bloque.posicion.y)}
                        onChange={(e) => {
                          const nuevaPosicion = {
                            ...bloque.posicion,
                            y: parseInt(e.target.value) || 0
                          };
                          moverBloque(bloque.id, nuevaPosicion);
                        }}
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: brandIdentity.typography.sizes.xs,
                          fontFamily: brandIdentity.typography.fontFamily
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Propiedades específicas */}
                {tienePropiedades && (
                  <div>
                    <div style={{
                      fontSize: brandIdentity.typography.sizes.small,
                      fontWeight: brandIdentity.typography.weights.semibold,
                      color: brandIdentity.colors.textDark,
                      marginBottom: brandIdentity.spacing.sm
                    }}>
                      Configuración Específica
                    </div>

                    {tipoBloque.propiedades.map(propiedad => (
                      <div key={propiedad} style={{ marginBottom: brandIdentity.spacing.sm }}>
                        <label style={{
                          fontSize: brandIdentity.typography.sizes.small,
                          color: brandIdentity.colors.textDark,
                          display: 'block',
                          marginBottom: brandIdentity.spacing.xs
                        }}>
                          {propiedad === 'form_id' ? 'ID del Formulario:' :
                            propiedad === 'contract_id' ? 'ID del Contrato:' :
                              propiedad === 'title' ? 'Título dentro de la app:' :
                                propiedad === 'count' ? 'Cantidad de archivos:' :
                                  propiedad === 'description' ? 'Descripción / Instrucciones:' :
                                    propiedad === 'types_allowed' ? 'Tipos de archivo permitidos:' :
                                      propiedad === 'mandatory' ? '¿Es obligatorio?' :
                                        propiedad}
                        </label>

                        {propiedad === 'form_id' ? (
                          /* Selector de formularios */
                          <div>
                            <select
                              value={bloque.propiedades[propiedad] || ''}
                              onChange={(e) => {
                                const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                                const nuevaListaBloques = [...bloques];
                                nuevaListaBloques[bloqueIndex] = {
                                  ...nuevaListaBloques[bloqueIndex],
                                  propiedades: {
                                    ...nuevaListaBloques[bloqueIndex].propiedades,
                                    [propiedad]: e.target.value
                                  }
                                };
                                setBloques(nuevaListaBloques);
                              }}
                              disabled={cargandoFormularios || !flujoActual?.categoria}
                              style={{
                                width: '100%',
                                padding: brandIdentity.spacing.sm,
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: brandIdentity.typography.sizes.small,
                                fontFamily: brandIdentity.typography.fontFamily,
                                backgroundColor: (cargandoFormularios || !flujoActual?.categoria) ? '#F3F4F6' : brandIdentity.colors.white
                              }}
                            >
                              <option value="">Selecciona un formulario</option>
                              {formularios.map(formulario => (
                                <option key={formulario.collection_id} value={formulario.collection_id}>
                                  {formulario.title}
                                </option>
                              ))}
                            </select>
                            {cargandoFormularios && (
                              <div style={{
                                fontSize: brandIdentity.typography.sizes.xs,
                                color: brandIdentity.colors.textLight,
                                marginTop: '4px'
                              }}>
                                Cargando formularios...
                              </div>
                            )}
                            {!flujoActual?.categoria && (
                              <div style={{
                                fontSize: brandIdentity.typography.sizes.xs,
                                color: '#EF4444',
                                marginTop: '4px'
                              }}>
                                Selecciona una categoría en las propiedades del flujo para cargar formularios.
                              </div>
                            )}
                            {flujoActual?.categoria && formularios.length === 0 && !cargandoFormularios && (
                              <div style={{
                                fontSize: brandIdentity.typography.sizes.xs,
                                color: '#F59E0B',
                                marginTop: '4px'
                              }}>
                                No hay formularios disponibles para la categoría "{categorias.find(c => c.id === flujoActual.categoria)?.name || flujoActual.categoria}".
                              </div>
                            )}
                            {/* Mostrar detalles del formulario seleccionado */}
                            {bloque.propiedades[propiedad] && (() => {
                              const formularioSeleccionado = formularios.find(f => f.collection_id === bloque.propiedades[propiedad]);
                              if (!formularioSeleccionado) return null;

                              return (
                                <div style={{
                                  marginTop: '6px',
                                  padding: brandIdentity.spacing.xs,
                                  backgroundColor: '#F0F9FF',
                                  border: '1px solid #BAE6FD',
                                  borderRadius: '4px',
                                  fontSize: brandIdentity.typography.sizes.xs
                                }}>
                                  <div style={{ fontWeight: brandIdentity.typography.weights.medium, color: '#0369A1', marginBottom: '2px' }}>
                                    📝 {formularioSeleccionado.title}
                                  </div>
                                  {formularioSeleccionado.description && (
                                    <div style={{ color: '#0284C7', marginBottom: '2px' }}>
                                      {formularioSeleccionado.description}
                                    </div>
                                  )}
                                  <div style={{ color: '#0284C7' }}>
                                    📱 {formularioSeleccionado.category} • ❓ {formularioSeleccionado.total_questions} preguntas
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        ) : propiedad === 'contract_id' ? (
                          /* Selector de contratos */
                          <div>
                            <select
                              value={bloque.propiedades[propiedad] || ''}
                              onChange={(e) => {
                                const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                                const nuevaListaBloques = [...bloques];
                                nuevaListaBloques[bloqueIndex] = {
                                  ...nuevaListaBloques[bloqueIndex],
                                  propiedades: {
                                    ...nuevaListaBloques[bloqueIndex].propiedades,
                                    [propiedad]: e.target.value
                                  }
                                };
                                setBloques(nuevaListaBloques);
                              }}
                              disabled={cargandoContratos}
                              style={{
                                width: '100%',
                                padding: brandIdentity.spacing.sm,
                                border: '1px solid #D1D5DB',
                                borderRadius: '6px',
                                fontSize: brandIdentity.typography.sizes.small,
                                fontFamily: brandIdentity.typography.fontFamily,
                                backgroundColor: cargandoContratos ? '#F3F4F6' : brandIdentity.colors.white
                              }}
                            >
                              <option value="">Selecciona un contrato</option>
                              {contratos.map(contrato => (
                                <option key={contrato.id} value={contrato.id}>
                                  {contrato.name}
                                </option>
                              ))}
                            </select>
                            {cargandoContratos && (
                              <div style={{
                                fontSize: brandIdentity.typography.sizes.xs,
                                color: brandIdentity.colors.textLight,
                                marginTop: '4px'
                              }}>
                                Cargando contratos...
                              </div>
                            )}
                            {contratos.length === 0 && !cargandoContratos && (
                              <div style={{
                                fontSize: brandIdentity.typography.sizes.xs,
                                color: '#F59E0B',
                                marginTop: '4px'
                              }}>
                                No hay contratos disponibles.
                              </div>
                            )}
                            {/* Mostrar detalles del contrato seleccionado */}
                            {bloque.propiedades[propiedad] && (() => {
                              const contratoSeleccionado = contratos.find(c => c.id === bloque.propiedades[propiedad]);
                              if (!contratoSeleccionado) return null;

                              return (
                                <div style={{
                                  marginTop: '6px',
                                  padding: brandIdentity.spacing.xs,
                                  backgroundColor: '#F0FDF4',
                                  border: '1px solid #BBF7D0',
                                  borderRadius: '4px',
                                  fontSize: brandIdentity.typography.sizes.xs
                                }}>
                                  <div style={{ fontWeight: brandIdentity.typography.weights.medium, color: '#166534', marginBottom: '2px' }}>
                                    📋 {contratoSeleccionado.name}
                                  </div>
                                  <div style={{ color: '#15803D' }}>
                                    🔖 Versión: {contratoSeleccionado.version}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        ) : propiedad === 'title' ? (
                          /* Título visible para el usuario en la app */
                          <input
                            type="text"
                            value={bloque.propiedades[propiedad] || ''}
                            onChange={(e) => {
                              const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                              const nuevaListaBloques = [...bloques];
                              nuevaListaBloques[bloqueIndex] = {
                                ...nuevaListaBloques[bloqueIndex],
                                propiedades: {
                                  ...nuevaListaBloques[bloqueIndex].propiedades,
                                  [propiedad]: e.target.value
                                }
                              };
                              setBloques(nuevaListaBloques);
                            }}
                            placeholder="Ej: Certificado Bancario"
                            style={{
                              width: '100%',
                              padding: brandIdentity.spacing.sm,
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: brandIdentity.typography.sizes.small,
                              fontFamily: brandIdentity.typography.fontFamily
                            }}
                          />
                        ) : propiedad === 'count' ? (
                          /* Cantidad de archivos */
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={bloque.propiedades[propiedad] ?? 1}
                            onChange={(e) => {
                              const parsed = Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 1));
                              const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                              const nuevaListaBloques = [...bloques];
                              nuevaListaBloques[bloqueIndex] = {
                                ...nuevaListaBloques[bloqueIndex],
                                propiedades: {
                                  ...nuevaListaBloques[bloqueIndex].propiedades,
                                  [propiedad]: parsed
                                }
                              };
                              setBloques(nuevaListaBloques);
                            }}
                            style={{
                              width: '100%',
                              padding: brandIdentity.spacing.sm,
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: brandIdentity.typography.sizes.small,
                              fontFamily: brandIdentity.typography.fontFamily
                            }}
                          />
                        ) : propiedad === 'description' ? (
                          /* Descripción / Instrucciones */
                          <input
                            type="text"
                            value={bloque.propiedades[propiedad] || ''}
                            onChange={(e) => {
                              const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                              const nuevaListaBloques = [...bloques];
                              nuevaListaBloques[bloqueIndex] = {
                                ...nuevaListaBloques[bloqueIndex],
                                propiedades: {
                                  ...nuevaListaBloques[bloqueIndex].propiedades,
                                  [propiedad]: e.target.value
                                }
                              };
                              setBloques(nuevaListaBloques);
                            }}
                            placeholder="Ej: Sube tu certificado bancario o documento de identidad"
                            style={{
                              width: '100%',
                              padding: brandIdentity.spacing.sm,
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: brandIdentity.typography.sizes.small,
                              fontFamily: brandIdentity.typography.fontFamily
                            }}
                          />
                        ) : propiedad === 'types_allowed' ? (
                          /* Selector de tipos de archivo permitidos */
                          <select
                            value={bloque.propiedades[propiedad] || 'both'}
                            onChange={(e) => {
                              const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                              const nuevaListaBloques = [...bloques];
                              nuevaListaBloques[bloqueIndex] = {
                                ...nuevaListaBloques[bloqueIndex],
                                propiedades: {
                                  ...nuevaListaBloques[bloqueIndex].propiedades,
                                  [propiedad]: e.target.value
                                }
                              };
                              setBloques(nuevaListaBloques);
                            }}
                            style={{
                              width: '100%',
                              padding: brandIdentity.spacing.sm,
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: brandIdentity.typography.sizes.small,
                              fontFamily: brandIdentity.typography.fontFamily,
                              backgroundColor: '#FFFFFF'
                            }}
                          >
                            <option value="files_only">Solo documentos (PDF, Word)</option>
                            <option value="photos_only">Solo fotos (JPG, PNG, HEIC, WEBP…)</option>
                            <option value="both">Documentos y fotos</option>
                          </select>
                        ) : propiedad === 'mandatory' ? (
                          /* Checkbox obligatorio */
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            <input
                              type="checkbox"
                              id={`mandatory_${bloque.id}`}
                              checked={bloque.propiedades[propiedad] ?? true}
                              onChange={(e) => {
                                const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                                const nuevaListaBloques = [...bloques];
                                nuevaListaBloques[bloqueIndex] = {
                                  ...nuevaListaBloques[bloqueIndex],
                                  propiedades: {
                                    ...nuevaListaBloques[bloqueIndex].propiedades,
                                    [propiedad]: e.target.checked
                                  }
                                };
                                setBloques(nuevaListaBloques);
                              }}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <label
                              htmlFor={`mandatory_${bloque.id}`}
                              style={{
                                fontSize: brandIdentity.typography.sizes.small,
                                color: brandIdentity.colors.textDark,
                                cursor: 'pointer'
                              }}
                            >
                              Los archivos son obligatorios
                            </label>
                          </div>
                        ) : (
                          /* Input por defecto para otras propiedades */
                          <input
                            type="text"
                            value={bloque.propiedades[propiedad] || ''}
                            onChange={(e) => {
                              const bloqueIndex = bloques.findIndex(b => b.id === bloque.id);
                              const nuevaListaBloques = [...bloques];
                              nuevaListaBloques[bloqueIndex] = {
                                ...nuevaListaBloques[bloqueIndex],
                                propiedades: {
                                  ...nuevaListaBloques[bloqueIndex].propiedades,
                                  [propiedad]: e.target.value
                                }
                              };
                              setBloques(nuevaListaBloques);
                            }}
                            placeholder={`Ingresa ${propiedad}`}
                            style={{
                              width: '100%',
                              padding: brandIdentity.spacing.sm,
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: brandIdentity.typography.sizes.small,
                              fontFamily: brandIdentity.typography.fontFamily
                            }}
                          />
                        )}

                        {propiedad !== 'form_id' && propiedad !== 'contract_id' &&
                          propiedad !== 'title' && propiedad !== 'count' &&
                          propiedad !== 'description' && propiedad !== 'types_allowed' && propiedad !== 'mandatory' && (
                            <div style={{
                              fontSize: brandIdentity.typography.sizes.xs,
                              color: brandIdentity.colors.textLight,
                              marginTop: '4px'
                            }}>
                              Configura esta propiedad específica del bloque.
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Acciones del bloque */}
                {bloque.tipo !== 'inicio' && bloque.tipo !== 'fin' && (
                  <div style={{
                    marginTop: brandIdentity.spacing.lg,
                    paddingTop: brandIdentity.spacing.md,
                    borderTop: '1px solid #E5E7EB'
                  }}>
                    <button
                      onClick={() => eliminarBloque(bloque.id)}
                      style={{
                        backgroundColor: '#EF4444',
                        color: brandIdentity.colors.white,
                        border: 'none',
                        padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
                        borderRadius: '6px',
                        fontSize: brandIdentity.typography.sizes.small,
                        fontWeight: brandIdentity.typography.weights.medium,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: brandIdentity.typography.fontFamily,
                        width: '100%'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#DC2626';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#EF4444';
                      }}
                    >
                      🗑️ Eliminar Bloque
                    </button>
                  </div>
                )}
              </div>
            );
          })() : flujoActual ? (
            /* Mostrar propiedades del flujo cuando no hay bloque seleccionado */
            <div>
              {/* Mostrar loading si el flujo se está cargando */}
              {flujoActual.cargando ? (
                <div style={{
                  textAlign: 'center',
                  padding: brandIdentity.spacing.lg,
                  color: brandIdentity.colors.textLight
                }}>
                  <div style={{ fontSize: '48px', marginBottom: brandIdentity.spacing.md }}>
                    ⏳
                  </div>
                  <p>Cargando datos del flujo...</p>
                </div>
              ) : (
                <div>
                  <div style={{
                    backgroundColor: '#F3F4F6',
                    padding: brandIdentity.spacing.md,
                    borderRadius: '8px',
                    marginBottom: brandIdentity.spacing.md
                  }}>
                    <div style={{
                      fontSize: brandIdentity.typography.sizes.body,
                      fontWeight: brandIdentity.typography.weights.semibold,
                      color: brandIdentity.colors.textDark,
                      marginBottom: brandIdentity.spacing.sm
                    }}>
                      📋 Propiedades del Flujo
                    </div>
                  </div>

                  {/* Nombre del flujo */}
                  <div style={{ marginBottom: brandIdentity.spacing.md }}>
                    <label style={{
                      fontSize: brandIdentity.typography.sizes.small,
                      color: brandIdentity.colors.textDark,
                      display: 'block',
                      marginBottom: brandIdentity.spacing.xs,
                      fontWeight: brandIdentity.typography.weights.medium
                    }}>
                      Nombre del Flujo: <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={flujoActual.nombre || ''}
                      onChange={(e) => {
                        const flujoActualizado = { ...flujoActual, nombre: e.target.value };
                        setFlujoActual(flujoActualizado);
                        const flujoIndex = flujos.findIndex(f => f.id === flujoActual.id);
                        if (flujoIndex !== -1) {
                          const nuevaListaFlujos = [...flujos];
                          nuevaListaFlujos[flujoIndex] = flujoActualizado;
                          setFlujos(nuevaListaFlujos);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: brandIdentity.spacing.sm,
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: brandIdentity.typography.sizes.small,
                        fontFamily: brandIdentity.typography.fontFamily
                      }}
                      placeholder="Nombre del flujo"
                    />
                  </div>

                  {/* Descripción del flujo */}
                  <div style={{ marginBottom: brandIdentity.spacing.md }}>
                    <label style={{
                      fontSize: brandIdentity.typography.sizes.small,
                      color: brandIdentity.colors.textDark,
                      display: 'block',
                      marginBottom: brandIdentity.spacing.xs,
                      fontWeight: brandIdentity.typography.weights.medium
                    }}>
                      Descripción:
                    </label>
                    <textarea
                      value={flujoActual.descripcion || ''}
                      onChange={(e) => {
                        const flujoActualizado = { ...flujoActual, descripcion: e.target.value };
                        setFlujoActual(flujoActualizado);
                        const flujoIndex = flujos.findIndex(f => f.id === flujoActual.id);
                        if (flujoIndex !== -1) {
                          const nuevaListaFlujos = [...flujos];
                          nuevaListaFlujos[flujoIndex] = flujoActualizado;
                          setFlujos(nuevaListaFlujos);
                        }
                      }}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: brandIdentity.spacing.sm,
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: brandIdentity.typography.sizes.small,
                        fontFamily: brandIdentity.typography.fontFamily,
                        resize: 'vertical'
                      }}
                      placeholder="Describe el propósito de este flujo..."
                    />
                  </div>

                  {/* Categoría del flujo */}
                  <div style={{ marginBottom: brandIdentity.spacing.md }}>
                    <label style={{
                      fontSize: brandIdentity.typography.sizes.small,
                      color: brandIdentity.colors.textDark,
                      display: 'block',
                      marginBottom: brandIdentity.spacing.xs,
                      fontWeight: brandIdentity.typography.weights.medium
                    }}>
                      Categoría: <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <select
                      value={flujoActual.categoria || ''}
                      onChange={(e) => {
                        const flujoActualizado = { ...flujoActual, categoria: e.target.value };
                        setFlujoActual(flujoActualizado);
                        const flujoIndex = flujos.findIndex(f => f.id === flujoActual.id);
                        if (flujoIndex !== -1) {
                          const nuevaListaFlujos = [...flujos];
                          nuevaListaFlujos[flujoIndex] = flujoActualizado;
                          setFlujos(nuevaListaFlujos);
                        }
                      }}
                      disabled={cargandoCategorias || errorCategorias || flujoActual?.cargando}
                      style={{
                        width: '100%',
                        padding: brandIdentity.spacing.sm,
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: brandIdentity.typography.sizes.small,
                        fontFamily: brandIdentity.typography.fontFamily,
                        backgroundColor: (cargandoCategorias || errorCategorias) ? '#F3F4F6' : brandIdentity.colors.white
                      }}
                    >
                      <option value="">Selecciona una categoría</option>
                      {categorias.map(categoria => (
                        <option key={categoria.id} value={categoria.id}>
                          {categoria.name}
                        </option>
                      ))}
                    </select>
                    {cargandoCategorias && (
                      <div style={{
                        fontSize: brandIdentity.typography.sizes.xs,
                        color: brandIdentity.colors.textLight,
                        marginTop: '4px'
                      }}>
                        Cargando categorías...
                      </div>
                    )}
                    {!cargandoCategorias && errorCategorias && (
                      <div style={{
                        fontSize: brandIdentity.typography.sizes.xs,
                        color: '#EF4444',
                        marginTop: '4px'
                      }}>
                        Error al cargar categorías.
                        <button
                          type="button"
                          style={{
                            marginLeft: '8px',
                            textDecoration: 'underline',
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: 'inherit'
                          }}
                          onClick={cargarCategorias}
                        >
                          Reintentar
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Información adicional */}
                  <div style={{
                    backgroundColor: '#F9FAFB',
                    padding: brandIdentity.spacing.sm,
                    borderRadius: '6px',
                    fontSize: brandIdentity.typography.sizes.xs,
                    color: brandIdentity.colors.textLight,
                    lineHeight: '1.4'
                  }}>
                    <div style={{ marginBottom: brandIdentity.spacing.xs }}>
                      <strong>Estadísticas:</strong>
                    </div>
                    <div>• {bloques.length} bloques totales</div>
                    <div>• {bloques.length - 2} bloques de proceso</div>
                    <div>• Creado: {new Date(flujoActual.fechaCreacion).toLocaleDateString()}</div>
                    <div>• Modificado: {new Date(flujoActual.fechaModificacion).toLocaleDateString()}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              color: brandIdentity.colors.textLight,
              paddingTop: brandIdentity.spacing.xl
            }}>
              <div style={{ fontSize: '48px', marginBottom: brandIdentity.spacing.md }}>
                📋
              </div>
              <p>Selecciona o crea un flujo para ver sus propiedades</p>
              <div style={{
                fontSize: brandIdentity.typography.sizes.xs,
                color: brandIdentity.colors.textLight,
                marginTop: brandIdentity.spacing.sm,
                fontStyle: 'italic'
              }}>
                También puedes seleccionar un bloque específico para editarlo
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notificaciones */}
      {mensaje && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: mensaje.tipo === 'error' ? '#FEE2E2' : '#D1FAE5',
          color: mensaje.tipo === 'error' ? '#991B1B' : '#047857',
          border: `1px solid ${mensaje.tipo === 'error' ? '#FECACA' : '#A7F3D0'}`,
          borderRadius: '8px',
          padding: brandIdentity.spacing.md,
          fontSize: brandIdentity.typography.sizes.small,
          fontWeight: brandIdentity.typography.weights.medium,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 2000,
          maxWidth: '400px',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          {mensaje.texto}
        </div>
      )}

      {/* Modal Nuevo Flujo */}
      {mostrarModalNuevoFlujo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000
        }}>
          <div style={{
            backgroundColor: brandIdentity.colors.white,
            borderRadius: '12px',
            padding: brandIdentity.spacing.lg,
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: brandIdentity.spacing.lg
            }}>
              <div>
                <h3 style={{
                  fontSize: brandIdentity.typography.sizes.h2,
                  fontWeight: brandIdentity.typography.weights.bold,
                  color: brandIdentity.colors.textDark,
                  margin: 0,
                  marginBottom: brandIdentity.spacing.xs
                }}>
                  🚀 Nuevo Flujo de Proceso
                </h3>
                <p style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: brandIdentity.colors.textLight,
                  margin: 0
                }}>
                  Crea un nuevo flujo para procesos de trade-in
                </p>
              </div>
              <button
                onClick={() => setMostrarModalNuevoFlujo(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: brandIdentity.colors.textLight,
                  padding: 0,
                  lineHeight: 1
                }}
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <div>
              {/* Nombre del flujo */}
              <div style={{ marginBottom: brandIdentity.spacing.md }}>
                <label style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: brandIdentity.colors.textDark,
                  display: 'block',
                  marginBottom: brandIdentity.spacing.xs,
                  fontWeight: brandIdentity.typography.weights.medium
                }}>
                  Nombre del Flujo <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={datosNuevoFlujo.nombre}
                  onChange={(e) => {
                    setDatosNuevoFlujo(prev => ({ ...prev, nombre: e.target.value }));
                    if (erroresModalFlujo.nombre) {
                      setErroresModalFlujo(prev => ({ ...prev, nombre: '' }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: brandIdentity.spacing.sm,
                    border: `1px solid ${erroresModalFlujo.nombre ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '6px',
                    fontSize: brandIdentity.typography.sizes.small,
                    fontFamily: brandIdentity.typography.fontFamily
                  }}
                  placeholder="Ej: Flujo Express para Smartphones"
                  maxLength={100}
                />
                {erroresModalFlujo.nombre && (
                  <div style={{
                    fontSize: brandIdentity.typography.sizes.xs,
                    color: '#EF4444',
                    marginTop: '4px'
                  }}>
                    {erroresModalFlujo.nombre}
                  </div>
                )}
              </div>

              {/* Descripción */}
              <div style={{ marginBottom: brandIdentity.spacing.md }}>
                <label style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: brandIdentity.colors.textDark,
                  display: 'block',
                  marginBottom: brandIdentity.spacing.xs,
                  fontWeight: brandIdentity.typography.weights.medium
                }}>
                  Descripción (Opcional)
                </label>
                <textarea
                  value={datosNuevoFlujo.descripcion}
                  onChange={(e) => setDatosNuevoFlujo(prev => ({ ...prev, descripcion: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: brandIdentity.spacing.sm,
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: brandIdentity.typography.sizes.small,
                    fontFamily: brandIdentity.typography.fontFamily,
                    resize: 'vertical'
                  }}
                  placeholder="Describe el propósito y alcance de este flujo..."
                  maxLength={300}
                />
                <div style={{
                  fontSize: brandIdentity.typography.sizes.xs,
                  color: brandIdentity.colors.textLight,
                  marginTop: '4px',
                  textAlign: 'right'
                }}>
                  {datosNuevoFlujo.descripcion.length}/300 caracteres
                </div>
              </div>

              {/* Categoría */}
              <div style={{ marginBottom: brandIdentity.spacing.lg }}>
                <label style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: brandIdentity.colors.textDark,
                  display: 'block',
                  marginBottom: brandIdentity.spacing.xs,
                  fontWeight: brandIdentity.typography.weights.medium
                }}>
                  Categoría <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  value={datosNuevoFlujo.categoria}
                  onChange={(e) => {
                    setDatosNuevoFlujo(prev => ({ ...prev, categoria: e.target.value }));
                    if (erroresModalFlujo.categoria) {
                      setErroresModalFlujo(prev => ({ ...prev, categoria: '' }));
                    }
                  }}
                  disabled={cargandoCategorias || errorCategorias}
                  style={{
                    width: '100%',
                    padding: brandIdentity.spacing.sm,
                    border: `1px solid ${erroresModalFlujo.categoria ? '#EF4444' : '#D1D5DB'}`,
                    borderRadius: '6px',
                    fontSize: brandIdentity.typography.sizes.small,
                    fontFamily: brandIdentity.typography.fontFamily,
                    backgroundColor: (cargandoCategorias || errorCategorias) ? '#F3F4F6' : brandIdentity.colors.white
                  }}
                >
                  <option value="">Selecciona una categoría</option>
                  {categorias.map(categoria => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.name}
                    </option>
                  ))}
                </select>
                {erroresModalFlujo.categoria && (
                  <div style={{
                    fontSize: brandIdentity.typography.sizes.xs,
                    color: '#EF4444',
                    marginTop: '4px'
                  }}>
                    {erroresModalFlujo.categoria}
                  </div>
                )}
                {cargandoCategorias && (
                  <div style={{
                    fontSize: brandIdentity.typography.sizes.xs,
                    color: brandIdentity.colors.textLight,
                    marginTop: '4px'
                  }}>
                    Cargando categorías...
                  </div>
                )}
                {!cargandoCategorias && errorCategorias && (
                  <div style={{
                    fontSize: brandIdentity.typography.sizes.xs,
                    color: '#EF4444',
                    marginTop: '4px'
                  }}>
                    No se pudieron cargar las categorías.
                    <button
                      type="button"
                      style={{
                        marginLeft: '8px',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        fontSize: 'inherit'
                      }}
                      onClick={cargarCategorias}
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Botones */}
            <div style={{
              display: 'flex',
              gap: brandIdentity.spacing.sm,
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setMostrarModalNuevoFlujo(false)}
                style={{
                  backgroundColor: 'transparent',
                  color: brandIdentity.colors.textDark,
                  border: `1px solid #D1D5DB`,
                  padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
                  borderRadius: '6px',
                  fontSize: brandIdentity.typography.sizes.small,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: brandIdentity.typography.fontFamily
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#F3F4F6';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                ↶ Cancelar
              </button>
              <button
                onClick={crearFlujoDesdeModal}
                style={{
                  backgroundColor: brandIdentity.colors.primary,
                  color: brandIdentity.colors.white,
                  border: 'none',
                  padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
                  borderRadius: '6px',
                  fontSize: brandIdentity.typography.sizes.small,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: brandIdentity.typography.fontFamily
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = brandIdentity.colors.primaryDark;
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = brandIdentity.colors.primary;
                }}
              >
                ✨ Crear Flujo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {mostrarModalEliminacion && flujoAEliminar && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000
        }}>
          <div style={{
            backgroundColor: brandIdentity.colors.white,
            borderRadius: '12px',
            padding: brandIdentity.spacing.lg,
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: brandIdentity.spacing.lg
            }}>
              <div>
                <h3 style={{
                  fontSize: brandIdentity.typography.sizes.h2,
                  fontWeight: brandIdentity.typography.weights.bold,
                  color: '#EF4444',
                  margin: 0,
                  marginBottom: brandIdentity.spacing.xs
                }}>
                  🗑️ Confirmar Eliminación
                </h3>
                <p style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: brandIdentity.colors.textLight,
                  margin: 0
                }}>
                  Esta acción no se puede deshacer
                </p>
              </div>
              <button
                onClick={() => {
                  setMostrarModalEliminacion(false);
                  setFlujoAEliminar(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: brandIdentity.colors.textLight,
                  padding: 0,
                  lineHeight: 1
                }}
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: brandIdentity.spacing.lg }}>
              <p style={{
                fontSize: brandIdentity.typography.sizes.body,
                color: brandIdentity.colors.textDark,
                margin: 0,
                marginBottom: brandIdentity.spacing.md
              }}>
                ¿Estás seguro de que deseas eliminar el flujo
                <strong> "{flujoAEliminar.nombre}"</strong>?
              </p>

              <div style={{
                backgroundColor: '#FEF3C7',
                border: '1px solid #F59E0B',
                borderRadius: '8px',
                padding: brandIdentity.spacing.md,
                marginBottom: brandIdentity.spacing.md
              }}>
                <div style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: '#92400E',
                  fontWeight: brandIdentity.typography.weights.medium,
                  marginBottom: brandIdentity.spacing.xs
                }}>
                  ⚠️ Importante:
                </div>
                <div style={{
                  fontSize: brandIdentity.typography.sizes.small,
                  color: '#92400E',
                  lineHeight: '1.4'
                }}>
                  Aunque elimines este flujo, si ya fue asignado a algún aliado comercial,
                  seguirá apareciendo a los usuarios de dicho aliado hasta que no lo cambies
                  por otro flujo en la sección de administración de aliados.
                </div>
              </div>
            </div>

            {/* Botones */}
            <div style={{
              display: 'flex',
              gap: brandIdentity.spacing.sm,
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setMostrarModalEliminacion(false);
                  setFlujoAEliminar(null);
                }}
                disabled={guardando}
                style={{
                  backgroundColor: 'transparent',
                  color: brandIdentity.colors.textDark,
                  border: `1px solid #D1D5DB`,
                  padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
                  borderRadius: '6px',
                  fontSize: brandIdentity.typography.sizes.small,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: brandIdentity.typography.fontFamily,
                  opacity: guardando ? 0.6 : 1
                }}
                onMouseOver={(e) => {
                  if (!guardando) e.target.style.backgroundColor = '#F3F4F6';
                }}
                onMouseOut={(e) => {
                  if (!guardando) e.target.style.backgroundColor = 'transparent';
                }}
              >
                ↶ Cancelar
              </button>
              <button
                onClick={eliminarFlujoConfirmado}
                disabled={guardando}
                style={{
                  backgroundColor: guardando ? '#9CA3AF' : '#EF4444',
                  color: brandIdentity.colors.white,
                  border: 'none',
                  padding: `${brandIdentity.spacing.sm} ${brandIdentity.spacing.md}`,
                  borderRadius: '6px',
                  fontSize: brandIdentity.typography.sizes.small,
                  fontWeight: brandIdentity.typography.weights.medium,
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: brandIdentity.typography.fontFamily
                }}
                onMouseOver={(e) => {
                  if (!guardando) e.target.style.backgroundColor = '#DC2626';
                }}
                onMouseOut={(e) => {
                  if (!guardando) e.target.style.backgroundColor = '#EF4444';
                }}
              >
                {guardando ? '⏳ Eliminando...' : '🗑️ Sí, Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
}

export default GestionFlujos;